import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import styled from 'styled-components'
import {AuthContext} from '../../../contexts/auth'
import {HistoryContext} from '../../../contexts/history'
import AiDisclaimer from '../../../globalComponents/AiDisclaimer'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    MenuItem,
    Modal,
    Skeleton,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LinkIcon from '@mui/icons-material/Link'
import {PriceResearchApiService} from '../../../services/PriceResearchApi.service'
import {AssistantApi} from '../../../services/AssistantApi.service'
import {
    AnexoOut,
    AssistenteOut,
    EventoOut,
    PERIODOS_MESES,
    PrecoResultado,
    PriceSearchDetail,
    PriceSearchOut,
    STATUS_FINAIS,
    StatusPesquisa,
} from '../../../types/priceResearch'
import {getCurrentColors} from '../../../utils/colors'

const COLORS = getCurrentColors()
const UFS = [
    'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB',
    'PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
]
const ACCENT = COLORS?.primariaEscura || '#3394EF'
// Acima deste nº de itens, confirmamos com o usuário antes de gastar créditos (1 Consulta/item).
const LIMIAR_CONFIRMA_ITENS = 5
// Teto de itens por pesquisa — espelha o PESQUISA_MAX_ITENS do backend (padrão 50).
// Decisão do dono: o limite antigo de 10 subiu para 50; 1 crédito por item permanece.
const MAX_ITENS_PESQUISA = 50

type Modo = 'unico' | 'multiplo' | 'upload'
type View = 'list' | 'form' | 'detail'

// Poll de reconciliação do assistente de IA (rede de segurança quando o notifier
// não entrega o push): intervalo entre tentativas e teto total antes de desistir.
// Correção deliberada sobre o incidente já conhecido do Carona (fallback single-shot
// de ~8s ficava estruturalmente curto demais e travava o botão) — aqui o teto
// também precisa manter margem real sobre os até 120s que este endpoint já levava
// de forma síncrona antes da conversão para async.
const ASSISTENTE_POLL_INTERVAL_MS = 5000
const ASSISTENTE_POLL_TIMEOUT_MS = 240000

const Container = styled.div`
    flex: 1;
    height: 100vh;
    overflow-y: auto;
    padding: 28px 40px;
    background-color: #f6f8fa;
    @media (max-width: 640px) {
        padding: 18px 14px;
    }
`
const Card = styled.div`
    background: #ffffff;
    border: 1px solid #e6e9ee;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 22px;
`
const SectionTitle = styled.h3`
    font-size: 17px;
    font-weight: 700;
    color: #1f2b3a;
    margin: 0 0 16px 0;
`
const ModeGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    margin-bottom: 22px;
    @media (max-width: 820px) {
        grid-template-columns: 1fr;
    }
`
const ModeCard = styled.button<{active: boolean}>`
    text-align: left;
    cursor: pointer;
    border-radius: 12px;
    padding: 14px 16px;
    background: ${({active}) => (active ? '#eef5ff' : '#ffffff')};
    border: 1.5px solid ${({active}) => (active ? ACCENT : '#e0e4ea')};
    display: flex;
    gap: 10px;
    align-items: flex-start;
    transition: all 0.15s;
    &:hover {
        border-color: ${ACCENT};
    }
`
const FieldLabel = styled.label`
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #3a3a3a;
    margin: 0 0 6px 2px;
`
const Th = styled.th`
    text-align: left;
    font-size: 12px;
    font-weight: 700;
    color: #7a8896;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    padding: 10px 12px;
    border-bottom: 1px solid #eef1f4;
`
const Td = styled.td`
    padding: 12px;
    border-bottom: 1px solid #f1f3f6;
    font-size: 14px;
    color: #2a3340;
    vertical-align: middle;
`

const currency = (v?: number | null) =>
    v == null
        ? '—'
        : new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(Number(v))
const dateTime = (iso?: string | null) => {
    if (!iso) return '—'
    // Backend grava UTC sem offset; trata como UTC e exibe no fuso de Brasília.
    const s = /[Zz]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z'
    return new Date(s).toLocaleString('pt-BR', {timeZone: 'America/Sao_Paulo'})
}
// Só a hora, com segundos (para o feed de progresso ao vivo).
const horaExata = (iso?: string | null) => {
    if (!iso) return ''
    const s = /[Zz]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z'
    return new Date(s).toLocaleTimeString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
}

const STATUS_LABEL: Record<StatusPesquisa, string> = {
    PENDENTE: 'Pendente',
    PARSEANDO: 'Lendo itens',
    COLETANDO: 'Coletando preços',
    AGUARDANDO_LLM: 'Analisando com Licito.Guru IA',
    CALCULANDO: 'Calculando',
    GERANDO_RELATORIO: 'Gerando relatório',
    CONCLUIDO: 'Concluído',
    ERRO: 'Erro',
}
const statusColor = (
    s: StatusPesquisa
): 'default' | 'info' | 'success' | 'error' => {
    if (s === 'CONCLUIDO') return 'success'
    if (s === 'ERRO') return 'error'
    if (s === 'PENDENTE') return 'default'
    return 'info'
}
const modoLabel = (p: PriceSearchOut) =>
    p.itens_total > 1 ? 'Múltiplos itens' : 'Item único'

// Vínculo de origem entre módulos (origem_modulo/origem_ref_id do backend).
// Mapa de rótulos — módulo novo é só mais uma linha aqui; valor desconhecido
// cai no genérico "Vinculada" (nunca quebra a tela).
const ORIGEM_MODULO_LABEL: Record<string, {curto: string; longo: string}> = {
    carona: {curto: 'Ata Inteligente', longo: 'Vinculada à Ata Inteligente'},
    levantamento: {
        curto: 'Levantamento de Mercado',
        longo: 'Vinculada ao Levantamento de Mercado',
    },
}
const origemLabel = (modulo: string) =>
    ORIGEM_MODULO_LABEL[modulo] ?? {curto: 'Vinculada', longo: 'Vinculada'}

// Eventos em linguagem clara para servidores (trilha de auditoria — art. 3º da IN 65).
const EVENTO_TITULO: Record<string, string> = {
    ENTRADA: 'Pesquisa registrada',
    PARSEANDO: 'Itens interpretados',
    COLETA_PNCP: 'Busca no PNCP (contratações públicas)',
    BUSCA_REFORMULADA: 'Termo de busca aperfeiçoado (Licito.Guru IA)',
    COLETA_WEB: 'Busca no varejo (mercado)',
    COLETA: 'Preços reunidos para análise',
    EQUALIZACAO: 'Padronização e análise dos preços',
    ESTATISTICA: 'Cálculo do valor de referência (IN 65)',
    RETRY: 'Ampliação da busca',
    REVISAO: 'Revisão manual da cesta',
    CONSUMO: 'Crédito de Consulta',
    GERANDO_RELATORIO: 'Geração do relatório',
    CONCLUIDO: 'Pesquisa concluída',
}
const eventoTitulo = (etapa: string) => EVENTO_TITULO[etapa] ?? etapa.replace(/_/g, ' ').toLowerCase()
const eventoDescricao = (ev: EventoOut) =>
    ev.etapa === 'ENTRADA' ? 'Dados da solicitação registrados.' : ev.detalhe || ''

export default function PriceResearch() {
    const api = useMemo(() => new PriceResearchApiService(), [])
    const navigate = useNavigate()
    const {userInfo} = useContext(AuthContext)
    const {
        priceResearchSocketMessage,
        assistenteSocketMessage,
        archivedPesquisas,
        archivedPesquisasPage,
        goToArchivedPesquisasPage,
        archivePesquisa,
        unarchivePesquisa,
        unarchieveModal,
        setUnarchieveModal,
    } = useContext(HistoryContext)
    const nomeUsuario = `${userInfo?.first_name || ''} ${userInfo?.last_name || ''}`.trim()
    const orgaoUsuario = userInfo?.organization?.name || ''
    const [view, setView] = useState<View>('list')

    // ---- formulário ----
    const [modo, setModo] = useState<Modo>('unico')
    const [descricao, setDescricao] = useState('')
    const [catmat, setCatmat] = useState('')
    const [quantidade, setQuantidade] = useState<number>(1)
    const [unidade, setUnidade] = useState('Unidade')
    const [listaItens, setListaItens] = useState('')
    const [arquivo, setArquivo] = useState<File | null>(null)
    // INSUMO DE PREÇO — não confundir com o `arquivo` acima, que é o TR/ETP e responde
    // "o quê pesquisar". Aqui o PDF responde "com quais preços": é um relatório de
    // pesquisa já feito em outra plataforma, cujos números o responsável quer aproveitar.
    // Sobem ANTES do create porque a coleta dispara junto com ele — e porque assim o
    // usuário vê o que foi lido antes de gastar crédito.
    const [anexos, setAnexos] = useState<File[]>([])
    const [anexosLidos, setAnexosLidos] = useState<AnexoOut[]>([])
    const [anexoAviso, setAnexoAviso] = useState<string>('')
    const [anexoErro, setAnexoErro] = useState<string | null>(null)
    const [lendoAnexos, setLendoAnexos] = useState(false)
    const [uf, setUf] = useState('')
    const [periodo, setPeriodo] = useState<number>(12)
    const [observacoes, setObservacoes] = useState('')
    const [processo, setProcesso] = useState('')
    const [orgao, setOrgao] = useState('')
    const [responsavel, setResponsavel] = useState('')
    const [iaBusy, setIaBusy] = useState<string | null>(null)
    // Assistente de IA (async): id + campo + callback de aplicação do único request
    // em voo. Nunca um mapa — a tela já serializa (um só iaBusy por vez).
    const pendenteAssistenteRef = useRef<{
        id: string
        campo: string
        aplicar: (r: any) => void
    } | null>(null)
    // Timer do poll de reconciliação (setTimeout recursivo) — rede de segurança caso
    // o push do notifier não chegue. Cancelado ao resolver (WS/poll) e no unmount.
    const assistentePollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [sugerirObjeto, setSugerirObjeto] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [formError, setFormError] = useState<string | null>(null)
    // Guardrail de crédito + confirmação de pesquisas grandes (>5 itens).
    const [saldo, setSaldo] = useState<number | null>(null)
    const [confirmItems, setConfirmItems] = useState<any[] | null>(null)
    // Melhoria de descrição por IA (marca→genérico) pendente de confirmação do usuário.
    const [sugestaoDesc, setSugestaoDesc] = useState<{
        descricao_sugerida: string
        marca_detectada: string
        termo_generico: string
        motivo: string
    } | null>(null)
    // Revisão automática no "Iniciar": IA sugere redação melhor e pede confirmação (1 clique).
    const [analisando, setAnalisando] = useState(false)
    const [melhoriaPendente, setMelhoriaPendente] = useState<{
        original: string
        sugerida: string
        marca: string
        termo: string
        motivo: string
    } | null>(null)
    // Várias linhas digitadas na aba de item único → oferece migrar para o modo múltiplo.
    const [linhasUnico, setLinhasUnico] = useState<string[] | null>(null)

    // Handoff do Levantamento de Mercado (aditivo): chegando com
    // state {origem:'levantamento', ...}, pré-preenche a descrição do item único
    // e guarda o par levantamento/alternativa p/ vincular a pesquisa criada.
    // Lido DIRETO de window.history.state.usr (onde o router grava o state da
    // navegação same-tab) — mesma leitura direta dos leitores do CaronaManager:
    // leitura e consumo no MESMO lugar (replaceState). O useLocation guardaria
    // uma cópia stale do state após o consumo, pronta p/ re-armar num remount.
    const handoffLevantamentoRef = useRef<{
        levantamento_id: string
        alternativa_id: string
    } | null>(null)
    useEffect(() => {
        const st = ((window.history.state as any)?.usr ?? {}) as {
            origem?: string
            levantamento_id?: string
            alternativa_id?: string
            descricao_solucao?: string
        }
        if (st.origem !== 'levantamento') return
        if (st.levantamento_id && st.alternativa_id) {
            handoffLevantamentoRef.current = {
                levantamento_id: st.levantamento_id,
                alternativa_id: st.alternativa_id,
            }
        }
        if (st.descricao_solucao) {
            setModo('unico')
            setDescricao(st.descricao_solucao)
        }
        setView('form')
        // Consome o state UMA única vez: sem isto, reload/back re-armavam o
        // handoff (history.state sobrevive ao F5) e uma pesquisa QUALQUER criada
        // depois sobrescreveria o vínculo da alternativa. DIRETO no history
        // (nunca navigate): navegar gera um location.key novo e o
        // RemountOnNavigate do Routes remonta o módulo — o formulário
        // recém-preenchido voltava vazio (regressão do LCTO-1639).
        window.history.replaceState(
            {...(window.history.state as any), usr: null},
            ''
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ---- lista / detalhe ----
    const [list, setList] = useState<PriceSearchOut[]>([])
    const [loadingList, setLoadingList] = useState(true)
    const [listError, setListError] = useState<string | null>(null)
    const [filtro, setFiltro] = useState('')
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [detail, setDetail] = useState<PriceSearchDetail | null>(null)
    const [loadingDetail, setLoadingDetail] = useState(false)
    const [gerando, setGerando] = useState<string | null>(null)

    // Handoff do Ata Inteligente (aditivo — mesmo contrato de state do
    // Levantamento de Mercado): chegando com {origem:'carona', ...},
    // pré-preenche a descrição do item
    // único e guarda a justificativa p/ vincular a pesquisa criada (pós-202).
    const handoffCaronaRef = useRef<{justificativa_id: string} | null>(null)
    useEffect(() => {
        // Duas portas de entrada: state (mesma aba) OU query string (a Ata abre
        // a Pesquisa em NOVA ABA — window.open não carrega state do router, então
        // o handoff viaja na URL e é consumido/limpo aqui do mesmo jeito).
        //
        // Por que a query string, e não um canal "invisível"? A Ata abre a nova aba
        // com window.open(..., 'noopener'): o contexto criado NÃO tem opener, e um
        // contexto sem opener nasce com sessionStorage VAZIO (não herda o da aba de
        // origem) — logo sessionStorage/postMessage não estão disponíveis aqui, e o
        // history.state do router também não cruza o limite da aba. Sobra a URL.
        // Por isso o handoff só carrega o que já é do próprio usuário (o id da
        // justificativa e a descrição que ele mesmo montou), nunca dado sensível: a
        // URL fica visível na barra, no histórico e em qualquer Referer.
        // Melhoria futura: mandar SÓ o id na URL e este leitor buscar o resto no
        // backend — some a descrição da barra de endereço e o link fica estável.
        const qs = new URLSearchParams(window.location.search)
        const st = (qs.get('origem') === 'carona'
            ? {
                origem: 'carona',
                justificativa_id: qs.get('justificativa_id') || '',
                descricao_solucao: qs.get('descricao_solucao') || '',
            }
            : ((window.history.state as any)?.usr ?? {})) as {
            origem?: string
            justificativa_id?: string
            descricao_solucao?: string
        }
        if (st.origem !== 'carona') return
        if (st.justificativa_id) {
            handoffCaronaRef.current = {justificativa_id: st.justificativa_id}
        }
        if (st.descricao_solucao) {
            setModo('unico')
            setDescricao(st.descricao_solucao)
        }
        setView('form')
        // Consome o state/query UMA única vez: sem isto, reload/back re-armavam o
        // handoff (history.state e a URL sobrevivem ao F5) e uma pesquisa QUALQUER
        // criada depois sobrescreveria o vínculo da justificativa. DIRETO no
        // history (nunca navigate): navegar gera um location.key novo e o
        // RemountOnNavigate do Routes remonta o módulo — era ESTE remount que
        // descartava o formulário logo depois do pré-preenchimento (regressão
        // do LCTO-1639; a aba abria, mas chegava vazia).
        // O consumo espelha o leitor do CaronaManager, porta a porta: limpa a
        // querystring SÓ quando a porta foi a URL (senão apagaria params
        // alheios de um deep link) e zera o usr SÓ quando havia state.
        if (qs.get('origem') === 'carona') {
            window.history.replaceState(
                window.history.state,
                '',
                window.location.pathname
            )
        }
        if ((window.history.state as any)?.usr) {
            window.history.replaceState(
                {...(window.history.state as any), usr: null},
                ''
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ---- feedback ----
    const [feedbackModal, setFeedbackModal] = useState(false)
    const [feedbackTarget, setFeedbackTarget] = useState<{
        uuid: string
        name: string
    } | null>(null)
    const [feedbackText, setFeedbackText] = useState('')
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)
    const [refazendo, setRefazendo] = useState(false)
    const [feedbackToast, setFeedbackToast] = useState<{
        open: boolean
        message: string
        severity: 'success' | 'error'
    }>({open: false, message: '', severity: 'success'})

    // ---- arquivamento ----
    const [archiveTarget, setArchiveTarget] = useState<PriceSearchOut | null>(
        null
    )

    const handleOpenArchiveModal = (p: PriceSearchOut) => {
        setArchiveTarget(p)
    }

    const handleCloseArchiveModal = () => {
        setArchiveTarget(null)
    }

    const handleConfirmArchive = async () => {
        if (!archiveTarget) return
        await archivePesquisa(archiveTarget.id)
        handleCloseArchiveModal()
        loadList()
    }

    const handleUnarchive = async (id: string) => {
        await unarchivePesquisa(id)
        loadList()
    }

    const handleOpenFeedbackModal = (p: PriceSearchOut) => {
        setFeedbackTarget({
            uuid: p.id,
            name: p.objeto_geral || `Pesquisa ${p.id.slice(0, 8)}`,
        })
        setFeedbackText('')
        setFeedbackModal(true)
    }

    const handleCloseFeedbackModal = () => {
        setFeedbackModal(false)
        setFeedbackTarget(null)
        setFeedbackText('')
    }

    const handleSubmitFeedback = async () => {
        if (!feedbackTarget || !feedbackText.trim()) return

        setFeedbackSubmitting(true)
        try {
            const assistantApi = new AssistantApi()
            await assistantApi.reportModule({
                object_id: feedbackTarget.uuid,
                module: 'price_research',
                title: feedbackTarget.name,
                feedback: feedbackText,
            })
            setFeedbackToast({
                open: true,
                message: 'Obrigado pelo feedback.',
                severity: 'success',
            })
            handleCloseFeedbackModal()
        } catch (error) {
            console.error('Erro ao enviar feedback:', error)
            setFeedbackToast({
                open: true,
                message: 'Erro ao enviar feedback. Tente novamente.',
                severity: 'error',
            })
        } finally {
            setFeedbackSubmitting(false)
        }
    }

    const handleExport = async (key: string, fn: () => Promise<void>) => {
        setGerando(key)
        try {
            await fn()
        } catch (e) {
            console.error('Erro ao gerar documento:', e)
            alert('Não foi possível gerar o documento agora.')
        } finally {
            setGerando(null)
        }
    }

    const loadList = useCallback(async () => {
        try {
            setListError(null)
            const data = await api.listPesquisas(50, 0)
            setList(data.pesquisas ?? [])
        } catch (err) {
            console.error('Erro ao listar pesquisas:', err)
            setListError('Não foi possível carregar suas pesquisas.')
        } finally {
            setLoadingList(false)
        }
    }, [api])

    useEffect(() => {
        loadList()
    }, [loadList])

    const fetchDetail = useCallback(
        async (id: string) => {
            const d = await api.getPesquisa(id)
            setDetail(d)
            setList((prev) => prev.map((p) => (p.id === id ? {...p, ...d} : p)))
            return d
        },
        [api]
    )

    // Refazer uma pesquisa que terminou em ERRO: o backend reaproveita os itens já
    // concluídos e refaz só o que faltou. Daqui em diante o progresso chega por push.
    const handleRefazer = useCallback(async () => {
        if (!detail) return
        setRefazendo(true)
        try {
            await api.retentar(detail.id)
            await fetchDetail(detail.id)
            setFeedbackToast({
                open: true,
                message: 'Refazendo a pesquisa — acompanhe o progresso na tela.',
                severity: 'success',
            })
        } catch (e: any) {
            console.error('Erro ao refazer pesquisa:', e)
            setFeedbackToast({
                open: true,
                message:
                    e?.response?.data?.detail ||
                    'Não foi possível refazer a pesquisa agora.',
                severity: 'error',
            })
        } finally {
            setRefazendo(false)
        }
    }, [api, detail, fetchDetail])

    const selectPesquisa = useCallback(
        async (id: string) => {
            setSelectedId(id)
            setDetail(null)
            setView('detail')
            setLoadingDetail(true)
            try {
                // Carga inicial (1 request). Daqui em diante o progresso chega só por push
                // (notifier/websocket) — sem polling, igual ao analisador (R.E.D) e ao gerador.
                await fetchDetail(id)
            } catch (err) {
                console.error('Erro ao carregar detalhe:', err)
            } finally {
                setLoadingDetail(false)
            }
        },
        [fetchDetail]
    )

    // Tempo real: a cada push do notifier para a pesquisa aberta, recarrega o detalhe.
    // Push puro, sem polling — mesmo padrão do analisador e do gerador de documentos.
    useEffect(() => {
        const msg = (priceResearchSocketMessage || {}) as {
            pesquisa_id?: string
            status?: string
        }
        if (!selectedId || !msg.pesquisa_id || msg.pesquisa_id !== selectedId) return
        fetchDetail(selectedId).catch((e) =>
            console.error('Erro ao atualizar via push:', e)
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceResearchSocketMessage])

    // Tempo real do ANEXO: o enriquecimento por IA é assíncrono (SQS + callback), e o
    // fim dele chega por push do notifier com `anexo_id`. Aqui o push diz "mudou" e o
    // front busca o estado novo — mesmo padrão do efeito da pesquisa acima, sem timer.
    useEffect(() => {
        const msg = (priceResearchSocketMessage || {}) as {anexo_id?: string}
        if (!msg.anexo_id) return
        if (!anexosLidos.some((a) => a.id === msg.anexo_id)) return
        api.obterAnexo(msg.anexo_id)
            .then((atualizado) =>
                setAnexosLidos((prev) =>
                    prev.map((a) => (a.id === atualizado.id ? atualizado : a))
                )
            )
            .catch((e) => console.error('Erro ao atualizar o anexo via push:', e))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceResearchSocketMessage])

    const resetForm = () => {
        setDescricao('')
        setCatmat('')
        setQuantidade(1)
        setUnidade('Unidade')
        setListaItens('')
        setObservacoes('')
        setProcesso('')
        setOrgao(orgaoUsuario)
        setResponsavel(nomeUsuario)
        setFormError(null)
        // Estados de IA/confirmação: sem esta limpeza, sugestões e diálogos da pesquisa
        // anterior reaparecem (com dados velhos) ao abrir uma nova pesquisa.
        setSugestaoDesc(null)
        setMelhoriaPendente(null)
        setConfirmItems(null)
        setLinhasUnico(null)
        setSaldo(null)
        setAnexos([])
        setAnexosLidos([])
        setAnexoAviso('')
        setAnexoErro(null)
    }

    // Envia os PDFs de insumo e mostra o que foi lido, ANTES de criar a pesquisa.
    // O usuário confirma a leitura sem ter gasto crédito: se o arquivo era digitalizado
    // e rendeu zero preços, ele descobre agora e não depois de pagar.
    const lerAnexos = async () => {
        if (anexos.length === 0) return
        setLendoAnexos(true)
        setAnexoErro(null)
        try {
            const r = await api.anexarInsumos(anexos, periodo)
            setAnexosLidos(r.anexos || [])
            setAnexoAviso(r.aviso || '')
            setAnexos([])
        } catch (err: any) {
            const d = err?.response?.data?.detail
            setAnexoErro(
                typeof d === 'string' ? d : 'Não foi possível ler os documentos enviados.'
            )
        } finally {
            setLendoAnexos(false)
        }
    }

    const removerAnexoLido = (id: string) => {
        // Só desfaz o vínculo no create — o arquivo enviado fica no servidor, órfão e
        // inerte. Apagá-lo daqui exigiria um DELETE que não existe, e fingir que
        // apagamos seria pior do que a órfã.
        setAnexosLidos((prev) => prev.filter((a) => a.id !== id))
    }

    // Total de preços que os documentos deixaram DISPONÍVEIS. Só isso vira número na
    // tela: contar os recusados junto sugeriria que eles entram, e eles não entram.
    const precosDisponiveisDosAnexos = anexosLidos.reduce(
        (n, a) => n + (a.leitura?.disponiveis_para_inclusao?.length || 0),
        0
    )

    // Pré-preenche órgão e responsável com os dados do usuário logado (quando carregarem).
    useEffect(() => {
        setOrgao((o) => o || orgaoUsuario)
        setResponsavel((r) => r || nomeUsuario)
    }, [orgaoUsuario, nomeUsuario])

    // Assistente de IA (async): id do pendente + poll de reconciliação (rede de
    // segurança quando o notifier não entrega). Ref/efeito independentes do
    // pendente de pesquisa acima — nunca cruzam (assistenteSocketMessage aqui,
    // priceResearchSocketMessage lá).
    function pararPollAssistente() {
        if (assistentePollTimerRef.current !== null) {
            clearTimeout(assistentePollTimerRef.current)
            assistentePollTimerRef.current = null
        }
    }

    // Aplica o resultado terminal (CONCLUIDO/ERRO) do assistente pendente, vindo por
    // qualquer um dos dois caminhos (WS + GET de confirmação, ou o poll de
    // reconciliação). Só aqui o botão volta a ficar habilitado — nunca logo após o
    // POST resolver.
    function finalizarAssistente(r: AssistenteOut) {
        pararPollAssistente()
        const pendente = pendenteAssistenteRef.current
        pendenteAssistenteRef.current = null
        setIaBusy(null)
        if (!pendente) return
        if (r.status === 'CONCLUIDO') pendente.aplicar(r)
        else if (r.status === 'ERRO')
            setFormError(r.erro || 'Não foi possível gerar com a Licito.Guru IA agora.')
    }

    // Agenda a próxima rodada do poll de reconciliação: reconsulta o status a cada
    // ASSISTENTE_POLL_INTERVAL_MS até resolver (finalizarAssistente) ou até estourar
    // o teto de ASSISTENTE_POLL_TIMEOUT_MS, quando desiste — estado terminal próprio,
    // sem passar por finalizarAssistente (um timeout não é um status real do backend).
    function agendarPollAssistente(id: string, prazoFinal: number) {
        assistentePollTimerRef.current = setTimeout(async () => {
            if (pendenteAssistenteRef.current?.id !== id) return
            try {
                const r = await api.getAssistente(id)
                if (pendenteAssistenteRef.current?.id !== id) return
                if (r.status !== 'PENDENTE') {
                    finalizarAssistente(r)
                    return
                }
            } catch (e) {
                console.error('Erro ao consultar status do assistente:', e)
                // best-effort: a próxima rodada do poll tenta de novo
            }
            if (pendenteAssistenteRef.current?.id !== id) return
            if (Date.now() >= prazoFinal) {
                pararPollAssistente()
                pendenteAssistenteRef.current = null
                setIaBusy(null)
                setFormError(
                    'A Licito.Guru IA está demorando mais que o esperado. Tente novamente.'
                )
                return
            }
            agendarPollAssistente(id, prazoFinal)
        }, ASSISTENTE_POLL_INTERVAL_MS)
    }

    // Recebeu aviso do notifier (websocket) de que o assistente pendente concluiu →
    // busca o resultado via GET e aplica quando terminal. Independente do effect de
    // priceResearchSocketMessage acima: nunca toca selectedId/detail.
    useEffect(() => {
        const msg = (assistenteSocketMessage || {}) as {
            assistente_id?: string
            status?: string
        }
        const pendente = pendenteAssistenteRef.current
        if (!pendente || !msg.assistente_id || msg.assistente_id !== pendente.id) return
        api.getAssistente(pendente.id)
            .then((r) => {
                if (pendenteAssistenteRef.current?.id === pendente.id && r.status !== 'PENDENTE') {
                    finalizarAssistente(r)
                }
            })
            .catch((e) => console.error('Erro ao buscar resultado do assistente:', e))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assistenteSocketMessage])

    // Garante que o poll de reconciliação do assistente não continue rodando (e
    // tentando setState) depois que o componente desmontar.
    useEffect(() => {
        return () => pararPollAssistente()
    }, [])

    // Assistente "✨ preencher com IA" — assíncrono: o POST devolve só {id, status};
    // o resultado chega pelo push do notifier (effect acima) ou pelo poll de
    // reconciliação. setIaBusy(null) só acontece no estado terminal (WS, poll ou
    // falha imediata do POST) — nunca incondicionalmente num finally.
    const assistirIA = async (
        campo: string,
        dados: Record<string, any>,
        aplicar: (r: any) => void
    ) => {
        setFormError(null)
        setIaBusy(campo)
        try {
            const r = await api.assistente(campo, dados)
            pendenteAssistenteRef.current = {id: r.id, campo, aplicar}
            agendarPollAssistente(r.id, Date.now() + ASSISTENTE_POLL_TIMEOUT_MS)
        } catch (e) {
            console.error(e)
            setFormError('Não foi possível gerar com a Licito.Guru IA agora.')
            setIaBusy(null)
        }
    }

    // Monta o item único a partir do form (descrição pode vir da sugestão da IA).
    // `estrita`: o usuário REJEITOU a generalização ("Pesquisar como digitei") — o
    // backend então só simplifica o termo ao ampliar a busca, nunca troca o objeto.
    const itemUnico = (desc: string, marcaRef?: string, estrita?: boolean) => [
        {
            descricao: desc.trim(),
            quantidade: Number(quantidade) || 1,
            unidade: unidade.trim() || 'Unidade',
            catmat_catser: catmat.trim() || undefined,
            marca_referencia: marcaRef || undefined,
            busca_estrita: estrita || undefined,
        },
    ]

    // Revisão automática da redação no "Iniciar" (item único): a Licito.Guru IA melhora
    // o termo de busca e troca marca por termo genérico — SEMPRE com confirmação do
    // usuário (1 clique). Best-effort: se a IA falhar/demorar, segue com o texto original.
    const analisarECriar = async (desc: string) => {
        setAnalisando(true)
        try {
            // Contrato ASSÍNCRONO do assistente (LCTO-1552): o POST devolve 202 com
            // status PENDENTE e a sugestão chega depois (push/GET). Consulta o
            // resultado a cada 1,5s por até 25s (com o spinner visível); ERRO
            // encerra na hora. Se o backend responder síncrono (contrato antigo),
            // o loop simplesmente não roda — os campos já vêm na resposta.
            let r: any = await api.assistente('melhorar_descricao', {descricao: desc})
            const deadline = Date.now() + 25000
            while (r?.status === 'PENDENTE' && r?.id && Date.now() < deadline) {
                await new Promise((ok) => setTimeout(ok, 1500))
                r = await api.getAssistente(r.id)
            }
            if (r?.status === 'PENDENTE') {
                r = {erro: 'timeout'} // estourou os 25s: segue com o texto original
            }
            const sugerida = ((r as any)?.descricao_sugerida || '').trim()
            if (!r.erro && sugerida && sugerida.toLowerCase() !== desc.toLowerCase()) {
                setMelhoriaPendente({
                    original: desc,
                    sugerida,
                    marca: (r as any)?.marca_detectada || '',
                    termo: (r as any)?.termo_generico || '',
                    motivo: (r as any)?.motivo || '',
                })
                return
            }
        } catch (e) {
            console.error('Revisão automática indisponível:', e)
        } finally {
            setAnalisando(false)
        }
        await doCreate(itemUnico(desc))
    }

    const handleSubmit = async () => {
        setFormError(null)
        let items
        if (modo === 'unico') {
            if (descricao.trim().length < 5) {
                setFormError('Descreva o item com ao menos 5 caracteres.')
                return
            }
            // Várias linhas na aba de item único = provavelmente uma LISTA de itens
            // colada na aba errada (cada item deve ser pesquisado separadamente).
            // O GATILHO conta linhas "com cara de item" (>= 5 chars), mas o dialog
            // recebe TODAS as linhas não vazias — specs curtas ("16GB") não podem
            // sumir silenciosamente do texto do usuário.
            const linhasTodas = descricao
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0)
            const linhasItem = linhasTodas.filter((l) => l.length >= 5)
            if (linhasItem.length > 1) {
                setLinhasUnico(linhasTodas)
                return
            }
            await analisarECriar(descricao.trim())
            return
        } else if (modo === 'multiplo') {
            const linhas = listaItens
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length >= 5)
            if (linhas.length < 1) {
                setFormError('Informe ao menos um item (um por linha, mín. 5 caracteres).')
                return
            }
            if (linhas.length > MAX_ITENS_PESQUISA) {
                setFormError(`Máximo de ${MAX_ITENS_PESQUISA} itens por pesquisa.`)
                return
            }
            items = linhas.map((l) => ({
                descricao: l,
                quantidade: 1,
                unidade: 'Unidade',
            }))
        } else {
            // Upload de TR/ETP
            if (!arquivo) {
                setFormError('Selecione um arquivo PDF ou DOCX.')
                return
            }
            setSubmitting(true)
            try {
                const created = await api.uploadTrEtp(
                    arquivo,
                    periodo,
                    uf ? uf.toUpperCase() : undefined,
                    {processo: processo.trim() || undefined, orgao: orgao.trim() || undefined}
                )
                setList((prev) => [created, ...prev])
                setArquivo(null)
                resetForm()
                await selectPesquisa(created.id)
            } catch (err: any) {
                console.error('Erro no upload:', err)
                const d = err?.response?.data?.detail
                setFormError(typeof d === 'string' ? d : 'Falha ao processar o documento.')
            } finally {
                setSubmitting(false)
            }
            return
        }

        // Pesquisa grande → confirma o custo (1 Consulta por item) antes de disparar.
        if (items.length > LIMIAR_CONFIRMA_ITENS) {
            try {
                const s = await api.getSaldo()
                setSaldo(s?.disponivel ?? null)
            } catch {
                setSaldo(null)
            }
            setConfirmItems(items)
            return
        }
        await doCreate(items)
    }

    const doCreate = async (items: any[]) => {
        setSubmitting(true)
        // Lido ANTES do POST para carimbar a origem na PRÓPRIA criação (contrato
        // origem_modulo/origem_ref_id do price-research) — a tag "Vinculada à Ata
        // Inteligente" nasce com a pesquisa, sem depender do vínculo best-effort
        // abaixo. O ref só é consumido (nulado) após o 202: falha no POST não
        // perde o handoff.
        const origemCarona = handoffCaronaRef.current
        // Mesmo carimbo p/ o handoff do Levantamento de Mercado — a tag
        // "Vinculada ao Levantamento de Mercado" nasce com a pesquisa. Spread
        // ANTES do carona: no conflito (teórico), o carona prevalece.
        const origemLevantamento = handoffLevantamentoRef.current
        try {
            const created = await api.createPesquisa({
                modo_entrada: modo === 'unico' ? 'MANUAL_UNICO' : 'MANUAL_MULTIPLO',
                items,
                periodo_meses: periodo,
                uf: uf ? uf.toUpperCase() : undefined,
                observacoes: observacoes || undefined,
                processo: processo.trim() || undefined,
                orgao: orgao.trim() || undefined,
                responsavel: responsavel.trim() || undefined,
                // Vínculo dos anexos: vai NO create porque a coleta é agendada nele.
                ...(anexosLidos.length ? {anexo_ids: anexosLidos.map((a) => a.id)} : {}),
                ...(origemLevantamento
                    ? {
                          origem_modulo: 'levantamento',
                          origem_ref_id: origemLevantamento.levantamento_id,
                      }
                    : {}),
                ...(origemCarona
                    ? {
                          origem_modulo: 'carona',
                          origem_ref_id: origemCarona.justificativa_id,
                      }
                    : {}),
            })
            // Handoff do Levantamento de Mercado: vincula a pesquisa criada à
            // alternativa de origem (best-effort — falha só loga, nunca
            // atrapalha a pesquisa). Import dinâmico p/ manter o diff mínimo.
            const handoff = handoffLevantamentoRef.current
            if (handoff) {
                handoffLevantamentoRef.current = null
                // Vínculo com a alternativa de origem, CONFIÁVEL: dispara já na
                // criação (não ao concluir) e RETENTA em falha transitória — sem
                // isso um erro passageiro no POST deixava a alternativa órfã (o
                // levantamento nunca recebia o pesquisa_precos_id, logo nem o
                // "em andamento" nem o valor voltavam). Best-effort: nunca
                // bloqueia nem atrapalha a pesquisa; a promise sobrevive à
                // navegação SPA de volta ao Levantamento (mesmo runtime).
                void (async () => {
                    try {
                        const {LevantamentoApiService} = await import(
                            '../../../services/LevantamentoApi.service'
                        )
                        const apiLev = new LevantamentoApiService()
                        let ultimoErro: unknown
                        for (let tentativa = 0; tentativa < 4; tentativa++) {
                            try {
                                await apiLev.vincularPesquisa(
                                    handoff.levantamento_id,
                                    handoff.alternativa_id,
                                    created.id
                                )
                                return // vínculo firmado (endpoint é idempotente)
                            } catch (e) {
                                ultimoErro = e
                                await new Promise((r) =>
                                    setTimeout(r, 600 * (tentativa + 1))
                                )
                            }
                        }
                        console.error(
                            'Vínculo com o levantamento falhou após retentativas (best-effort):',
                            ultimoErro
                        )
                    } catch (e) {
                        console.error(
                            'Vínculo com o levantamento falhou (best-effort):',
                            e
                        )
                    }
                })()
            }
            setList((prev) => [created, ...prev])
            resetForm()
            await selectPesquisa(created.id)
            // Handoff do Ata Inteligente: vincula a pesquisa criada à justificativa
            // de origem. CONFIÁVEL: dispara na criação e RETENTA em falha
            // transitória (mesmo padrão do Levantamento) — sem isso um erro
            // passageiro no POST deixava a ata órfã (a justificativa nunca recebia
            // o pesquisa_precos_id). Best-effort: nunca bloqueia a pesquisa; a
            // promise sobrevive à navegação SPA de volta ao Ata (mesmo runtime).
            const handoffCarona = handoffCaronaRef.current
            if (handoffCarona) {
                handoffCaronaRef.current = null
                void (async () => {
                    try {
                        const {caronaApi} = await import(
                            '../../../services/CaronaApi.service'
                        )
                        let ultimoErro: unknown
                        for (let tentativa = 0; tentativa < 4; tentativa++) {
                            try {
                                await caronaApi.vincularPesquisa(
                                    handoffCarona.justificativa_id,
                                    created.id
                                )
                                return // vínculo firmado (endpoint é idempotente)
                            } catch (e) {
                                ultimoErro = e
                                await new Promise((r) =>
                                    setTimeout(r, 600 * (tentativa + 1))
                                )
                            }
                        }
                        console.error(
                            'Vínculo com a justificativa falhou após retentativas (best-effort):',
                            ultimoErro
                        )
                    } catch (e) {
                        console.error(
                            'Vínculo com a justificativa falhou (best-effort):',
                            e
                        )
                    }
                })()
            }
        } catch (err: any) {
            console.error('Erro ao criar pesquisa:', err)
            const d = err?.response?.data?.detail
            setFormError(typeof d === 'string' ? d : 'Falha ao criar a pesquisa.')
        } finally {
            setSubmitting(false)
        }
    }

    const filtrados = list.filter((p) =>
        (p.objeto_geral || '').toLowerCase().includes(filtro.toLowerCase())
    )

    // Nº de itens válidos digitados no modo "Vários itens" (mesmo critério do
    // handleSubmit: linha com ≥ 5 caracteres) — alimenta o rótulo do botão
    // "Pesquisar N itens (N créditos)": o custo aparece ANTES de disparar.
    // No upload de TR/ETP o nº só é conhecido após a extração — o rótulo padrão
    // permanece, e a copy do passo informa o custo por item identificado.
    const itensMultiplo =
        modo === 'multiplo'
            ? listaItens
                  .split('\n')
                  .map((l) => l.trim())
                  .filter((l) => l.length >= 5).length
            : 0
    const rotuloPesquisarN = (n: number) =>
        `Pesquisar ${n} ${n === 1 ? 'item' : 'itens'} (${n} ${n === 1 ? 'crédito' : 'créditos'})`

    return (
        <Container>
            {/* Cabeçalho */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0.5,
                }}
            >
                <Typography variant="h5" sx={{fontWeight: 700, color: '#1f2b3a'}}>
                    {view === 'form'
                        ? 'Nova pesquisa'
                        : view === 'detail'
                        ? 'Resultado da pesquisa'
                        : 'Pesquisa de Preços'}
                </Typography>
                {view !== 'list' && (
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => {
                            setView('list')
                            loadList()
                        }}
                        sx={{textTransform: 'none', fontWeight: 600, color: ACCENT}}
                    >
                        Voltar
                    </Button>
                )}
            </Box>
            <Typography variant="body2" sx={{color: '#5b6b7b', mb: 3}}>
                Integrado ao <strong>PNCP</strong> (valor homologado de editais e atas), ao{' '}
                <strong>Google Shopping</strong> (preços de fornecedores via API) e às tabelas
                oficiais <strong>ANP</strong> (combustíveis) e <strong>CMED</strong> (medicamentos).
                Equalização por <strong>Licito.Guru IA</strong> e valor de referência conforme a Lei 14.133/2021 e a IN
                SEGES/ME nº 65/2021.
            </Typography>

            {/* ---------------- FORM ---------------- */}
            {view === 'form' && (
                <>
                    <Card>
                        <SectionTitle>Modo de entrada</SectionTitle>
                        <ModeGrid>
                            <ModeCard active={modo === 'unico'} onClick={() => setModo('unico')} type="button">
                                <DescriptionOutlinedIcon sx={{color: ACCENT}} />
                                <span>
                                    <strong>Item único</strong>
                                    <br />
                                    <small style={{color: '#6b7888'}}>Descreva um item/serviço</small>
                                </span>
                            </ModeCard>
                            <ModeCard active={modo === 'multiplo'} onClick={() => setModo('multiplo')} type="button">
                                <FormatListBulletedIcon sx={{color: ACCENT}} />
                                <span>
                                    <strong>Múltiplos itens</strong>
                                    <br />
                                    <small style={{color: '#6b7888'}}>Um item por linha</small>
                                </span>
                            </ModeCard>
                            <ModeCard active={modo === 'upload'} onClick={() => setModo('upload')} type="button">
                                <UploadFileOutlinedIcon sx={{color: ACCENT}} />
                                <span>
                                    <strong>Upload de TR/ETP</strong>
                                    <br />
                                    <small style={{color: '#6b7888'}}>PDF ou DOCX — a Licito.Guru IA extrai os itens</small>
                                </span>
                            </ModeCard>
                        </ModeGrid>

                        {modo === 'unico' && (
                            <>
                                <FieldLabel>Descrição do item/serviço *</FieldLabel>
                                <TextField
                                    placeholder="Ex.: Computador desktop, processador 8 núcleos, 16GB RAM, SSD 512GB..."
                                    value={descricao}
                                    onChange={(e) => setDescricao(e.target.value)}
                                    multiline
                                    minRows={3}
                                    fullWidth
                                    size="small"
                                    sx={{mb: 0.5}}
                                />
                                <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 1}}>
                                    <Button
                                        disabled={iaBusy !== null || descricao.trim().length < 5}
                                        startIcon={
                                            iaBusy === 'melhorar_descricao' ? (
                                                <CircularProgress size={14} />
                                            ) : undefined
                                        }
                                        onClick={() =>
                                            assistirIA('melhorar_descricao', {descricao}, (r) =>
                                                setSugestaoDesc(r)
                                            )
                                        }
                                        sx={{textTransform: 'none', color: ACCENT, fontWeight: 600}}
                                    >
                                        ✨ Melhorar descrição com Licito.Guru IA
                                    </Button>
                                </Box>
                                {sugestaoDesc && (
                                    <Alert severity="info" sx={{mb: 2}}>
                                        {sugestaoDesc.marca_detectada && (
                                            <Typography sx={{fontSize: '.85rem', mb: 0.5}}>
                                                Detectamos a marca{' '}
                                                <strong>{sugestaoDesc.marca_detectada}</strong>. Buscar pelo
                                                termo genérico{' '}
                                                <strong>{sugestaoDesc.termo_generico || 'do objeto'}</strong>{' '}
                                                costuma ampliar os resultados no PNCP.
                                            </Typography>
                                        )}
                                        <Typography sx={{fontSize: '.9rem'}}>
                                            <strong>Sugestão:</strong> {sugestaoDesc.descricao_sugerida}
                                        </Typography>
                                        {sugestaoDesc.motivo && (
                                            <Typography sx={{fontSize: '.78rem', color: '#64748b', mt: 0.5}}>
                                                {sugestaoDesc.motivo}
                                            </Typography>
                                        )}
                                        <Box sx={{display: 'flex', gap: 1, mt: 1}}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    if (sugestaoDesc.descricao_sugerida)
                                                        setDescricao(sugestaoDesc.descricao_sugerida)
                                                    setSugestaoDesc(null)
                                                }}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    backgroundColor: ACCENT,
                                                    '&:hover': {backgroundColor: ACCENT},
                                                }}
                                            >
                                                Usar sugestão
                                            </Button>
                                            <Button
                                                size="small"
                                                onClick={() => setSugestaoDesc(null)}
                                                sx={{textTransform: 'none', color: '#64748b'}}
                                            >
                                                Manter minha descrição
                                            </Button>
                                        </Box>
                                    </Alert>
                                )}
                                <FieldLabel>Código CATMAT/CATSER (opcional)</FieldLabel>
                                <TextField
                                    value={catmat}
                                    onChange={(e) => setCatmat(e.target.value)}
                                    fullWidth
                                    size="small"
                                    sx={{mb: 2}}
                                />
                                <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                                    <Box sx={{flex: 1, minWidth: 160}}>
                                        <FieldLabel>Quantidade</FieldLabel>
                                        <TextField
                                            type="number"
                                            value={quantidade}
                                            onChange={(e) => setQuantidade(Number(e.target.value))}
                                            onWheel={(e) => (e.target as HTMLElement).blur()}
                                            fullWidth
                                            size="small"
                                            inputProps={{min: 1, step: 'any'}}
                                        />
                                    </Box>
                                    <Box sx={{flex: 1, minWidth: 160}}>
                                        <FieldLabel>Unidade de medida</FieldLabel>
                                        <TextField
                                            value={unidade}
                                            onChange={(e) => setUnidade(e.target.value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </Box>
                                </Box>
                            </>
                        )}

                        {modo === 'multiplo' && (
                            <>
                                <Box sx={{display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-end', flexWrap: 'wrap'}}>
                                    <Box sx={{flex: 1, minWidth: 220}}>
                                        <FieldLabel>Descreva o que precisa comprar (a Licito.Guru IA sugere os itens)</FieldLabel>
                                        <TextField
                                            value={sugerirObjeto}
                                            onChange={(e) => setSugerirObjeto(e.target.value)}
                                            placeholder="Ex.: material de escritório para a secretaria"
                                            fullWidth
                                            size="small"
                                        />
                                    </Box>
                                    <Button
                                        disabled={iaBusy !== null || sugerirObjeto.trim().length < 3}
                                        startIcon={iaBusy === 'sugerir_itens' ? <CircularProgress size={14} /> : undefined}
                                        onClick={() =>
                                            assistirIA('sugerir_itens', {texto: sugerirObjeto}, (r) => {
                                                if (r.itens?.length) setListaItens(r.itens.join('\n'))
                                            })
                                        }
                                        sx={{textTransform: 'none', color: ACCENT, fontWeight: 600, mb: 0.2}}
                                    >
                                        ✨ Sugerir itens com Licito.Guru IA
                                    </Button>
                                </Box>
                                <FieldLabel>Itens (um por linha) *</FieldLabel>
                                <TextField
                                    placeholder={'Caneta esferográfica azul ponta média\nResma de papel A4 75g\nGrampeador de mesa 26/6'}
                                    value={listaItens}
                                    onChange={(e) => setListaItens(e.target.value)}
                                    multiline
                                    minRows={5}
                                    fullWidth
                                    size="small"
                                />
                                <Typography variant="caption" sx={{color: '#7a8896'}}>
                                    Até {MAX_ITENS_PESQUISA} itens. Cada linha vira um item da
                                    pesquisa e consome 1 crédito de pesquisa.
                                </Typography>
                            </>
                        )}

                        {modo === 'upload' && (
                            <>
                                <FieldLabel>Documento (PDF ou DOCX) *</FieldLabel>
                                <input
                                    type="file"
                                    accept=".pdf,.docx"
                                    onChange={(e) => setArquivo(e.target.files?.[0] || null)}
                                />
                                <Typography variant="caption" sx={{display: 'block', color: '#7a8896', mt: 1}}>
                                    A Licito.Guru IA lê o Termo de Referência / ETP e extrai os itens automaticamente
                                    (até {MAX_ITENS_PESQUISA}). Cada item identificado consome 1 crédito de
                                    pesquisa. Pode levar alguns segundos.
                                </Typography>
                                {arquivo && (
                                    <Typography variant="caption" sx={{display: 'block', color: '#15803d', mt: 0.5}}>
                                        Selecionado: {arquivo.name}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Card>

                    {modo !== 'upload' && (
                        <Card>
                            <SectionTitle>
                                Já tem um relatório de preços? (opcional)
                            </SectionTitle>
                            <Typography sx={{fontSize: '.82rem', color: '#64748b', mb: 1.5}}>
                                Envie seu PDF com preços já levantados. Lemos os valores e os
                                deixamos disponíveis na revisão da cesta, caso queira
                                considerá-los.
                            </Typography>

                            {anexosLidos.length < 3 && (
                                <>
                                    <input
                                        type="file"
                                        accept=".pdf"
                                        multiple
                                        data-testid="anexos-insumo"
                                        disabled={lendoAnexos}
                                        onChange={(e) => {
                                            const escolhidos = Array.from(e.target.files || [])
                                            const vagas = 3 - anexosLidos.length
                                            setAnexos(escolhidos.slice(0, vagas))
                                            setAnexoErro(
                                                escolhidos.length > vagas
                                                    ? `Limite de 3 documentos: os ${vagas} primeiros foram considerados.`
                                                    : null
                                            )
                                        }}
                                    />
                                    {anexos.length > 0 && (
                                        <Box sx={{mt: 1.5}}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                disabled={lendoAnexos}
                                                onClick={lerAnexos}
                                            >
                                                {lendoAnexos
                                                    ? 'Lendo os documentos…'
                                                    : `Ler ${anexos.length} documento(s)`}
                                            </Button>
                                            <Typography
                                                variant="caption"
                                                sx={{display: 'block', color: '#7a8896', mt: 0.5}}
                                            >
                                                Nada é cobrado nesta etapa — você confere o que foi
                                                lido antes de criar a pesquisa.
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}

                            {anexoErro && (
                                <Typography
                                    variant="caption"
                                    sx={{display: 'block', color: '#b91c1c', mt: 1}}
                                >
                                    {anexoErro}
                                </Typography>
                            )}

                            {anexosLidos.map((a) => {
                                const disp = a.leitura?.disponiveis_para_inclusao?.length || 0
                                const rec = a.leitura?.recusados?.length || 0
                                const problema = a.status_extracao !== 'OK' || disp === 0
                                return (
                                    <Box
                                        key={a.id}
                                        sx={{
                                            mt: 1.5,
                                            p: 1.5,
                                            border: '1px solid',
                                            borderColor: problema ? '#fde68a' : '#bbf7d0',
                                            bgcolor: problema ? '#fffbeb' : '#f0fdf4',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                gap: 1,
                                            }}
                                        >
                                            <Typography sx={{fontSize: '.85rem', fontWeight: 600}}>
                                                {a.nome_arquivo}
                                                {a.paginas > 0 && (
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            fontWeight: 400,
                                                            color: '#64748b',
                                                            ml: 0.5,
                                                        }}
                                                    >
                                                        ({a.paginas} pág.)
                                                    </Typography>
                                                )}
                                            </Typography>
                                            <Button
                                                size="small"
                                                color="inherit"
                                                onClick={() => removerAnexoLido(a.id)}
                                                sx={{minWidth: 'auto', color: '#64748b'}}
                                            >
                                                Remover
                                            </Button>
                                        </Box>
                                        <Typography
                                            sx={{fontSize: '.8rem', color: '#475569', mt: 0.5}}
                                        >
                                            {disp > 0
                                                ? `${disp} preço(s) prontos para você incluir na revisão.`
                                                : a.mensagem}
                                        </Typography>
                                        {a.ia_status === 'PENDENTE' && (
                                            <Typography
                                                variant="caption"
                                                sx={{display: 'block', color: '#1e40af', mt: 0.5}}
                                            >
                                                Estamos tentando ler melhor este documento —
                                                o resultado aparece aqui sozinho quando ficar
                                                pronto. Você pode seguir normalmente.
                                            </Typography>
                                        )}
                                        {a.ia_status === 'FALHOU' && (
                                            <Typography
                                                variant="caption"
                                                sx={{display: 'block', color: '#b45309', mt: 0.5}}
                                            >
                                                Não foi possível fazer a leitura assistida deste
                                                documento. Os preços acima continuam valendo.
                                            </Typography>
                                        )}
                                        {rec > 0 && (
                                            <Typography
                                                variant="caption"
                                                sx={{display: 'block', color: '#7a8896', mt: 0.5}}
                                            >
                                                {rec} número(s) do documento foram descartados na
                                                leitura (médias, totais, datas fora do período ou
                                                fonte não identificada) e não serão oferecidos.
                                            </Typography>
                                        )}
                                        {!a.arquivo_guardado && (
                                            <Typography
                                                variant="caption"
                                                sx={{display: 'block', color: '#b45309', mt: 0.5}}
                                            >
                                                Atenção: não conseguimos guardar este arquivo. Os
                                                preços seguem disponíveis, mas você precisará juntar
                                                o PDF ao processo por conta própria.
                                            </Typography>
                                        )}
                                    </Box>
                                )
                            })}

                            {precosDisponiveisDosAnexos > 0 && (
                                <Typography
                                    variant="caption"
                                    sx={{display: 'block', color: '#475569', mt: 1.5}}
                                >
                                    {anexoAviso}
                                </Typography>
                            )}
                        </Card>
                    )}

                    <Card>
                        <SectionTitle>Identificação do processo (recomendado)</SectionTitle>
                        <Typography sx={{fontSize: '.82rem', color: '#64748b', mb: 1.5}}>
                            A IN 65/2021 (art. 3º) exige identificar o processo e o responsável no
                            documento de pesquisa. Se deixar em branco, usamos seu nome de usuário
                            como responsável.
                        </Typography>
                        <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                            <Box sx={{flex: 1, minWidth: 200}}>
                                <FieldLabel>Nº do processo administrativo</FieldLabel>
                                <TextField
                                    value={processo}
                                    onChange={(e) => setProcesso(e.target.value)}
                                    placeholder="Ex.: 23080.012345/2026-99"
                                    fullWidth
                                    size="small"
                                />
                            </Box>
                            <Box sx={{flex: 1, minWidth: 200}}>
                                <FieldLabel>Órgão / entidade</FieldLabel>
                                <TextField
                                    value={orgao}
                                    onChange={(e) => setOrgao(e.target.value)}
                                    placeholder="Ex.: Prefeitura Municipal de..."
                                    fullWidth
                                    size="small"
                                />
                            </Box>
                            <Box sx={{flex: 1, minWidth: 200}}>
                                <FieldLabel>Responsável pela pesquisa</FieldLabel>
                                <TextField
                                    value={responsavel}
                                    onChange={(e) => setResponsavel(e.target.value)}
                                    placeholder="(seu nome — opcional)"
                                    fullWidth
                                    size="small"
                                />
                            </Box>
                        </Box>
                    </Card>

                    <Card>
                        <SectionTitle>Parâmetros da pesquisa</SectionTitle>
                        <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2}}>
                            <Box sx={{flex: 1, minWidth: 200}}>
                                <FieldLabel>UF do órgão (opcional)</FieldLabel>
                                <TextField
                                    select
                                    value={uf}
                                    onChange={(e) => setUf(e.target.value)}
                                    fullWidth
                                    size="small"
                                >
                                    <MenuItem value="">— selecione —</MenuItem>
                                    {UFS.map((u) => (
                                        <MenuItem key={u} value={u}>
                                            {u}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                            <Box sx={{flex: 1, minWidth: 200}}>
                                <FieldLabel>Período de busca (meses retroativos)</FieldLabel>
                                <TextField
                                    select
                                    value={periodo}
                                    onChange={(e) => setPeriodo(Number(e.target.value))}
                                    fullWidth
                                    size="small"
                                >
                                    {PERIODOS_MESES.map((m) => (
                                        <MenuItem key={m} value={m}>
                                            {m} meses
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        </Box>
                        <FieldLabel>Observações adicionais</FieldLabel>
                        <TextField
                            value={observacoes}
                            onChange={(e) => setObservacoes(e.target.value)}
                            multiline
                            minRows={2}
                            fullWidth
                            size="small"
                        />
                    </Card>

                    {formError && (
                        <Alert severity="error" sx={{mb: 2}}>
                            {formError}
                        </Alert>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting || analisando}
                        startIcon={
                            submitting || analisando ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : undefined
                        }
                        sx={{textTransform: 'none', fontWeight: 600, backgroundColor: ACCENT, '&:hover': {backgroundColor: ACCENT}, px: 3, py: 1.1}}
                    >
                        {analisando
                            ? 'Revisando a descrição com a Licito.Guru IA...'
                            : submitting
                              ? 'Enviando...'
                              : itensMultiplo > 0
                                ? rotuloPesquisarN(itensMultiplo)
                                : 'Iniciar pesquisa de preços'}
                    </Button>

                    <Dialog open={confirmItems !== null} onClose={() => setConfirmItems(null)}>
                        <DialogTitle>
                            Confirmar pesquisa de {confirmItems?.length} itens
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText component="div">
                                Esta pesquisa tem <strong>{confirmItems?.length} itens</strong> e consome{' '}
                                <strong>1 crédito de Consulta por item</strong> — total de{' '}
                                <strong>{confirmItems?.length} Consultas</strong>.
                                {saldo !== null && (
                                    <div style={{marginTop: 8}}>
                                        Você tem <strong>{saldo}</strong>{' '}
                                        {saldo === 1 ? 'Consulta disponível' : 'Consultas disponíveis'}.
                                        {confirmItems && saldo < confirmItems.length && (
                                            <span
                                                style={{
                                                    color: '#b91c1c',
                                                    display: 'block',
                                                    marginTop: 4,
                                                }}
                                            >
                                                Saldo insuficiente para todos os itens — reduza a quantidade
                                                ou libere mais créditos.
                                            </span>
                                        )}
                                    </div>
                                )}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => setConfirmItems(null)}
                                sx={{textTransform: 'none', color: '#64748b'}}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                disabled={
                                    saldo !== null &&
                                    confirmItems !== null &&
                                    saldo < confirmItems.length
                                }
                                onClick={() => {
                                    const it = confirmItems
                                    setConfirmItems(null)
                                    if (it) doCreate(it)
                                }}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    backgroundColor: ACCENT,
                                    '&:hover': {backgroundColor: ACCENT},
                                }}
                            >
                                {confirmItems
                                    ? rotuloPesquisarN(confirmItems.length)
                                    : 'Confirmar e pesquisar'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={melhoriaPendente !== null} onClose={() => setMelhoriaPendente(null)}>
                        <DialogTitle>Sugestão da Licito.Guru IA</DialogTitle>
                        <DialogContent>
                            <DialogContentText component="div">
                                {melhoriaPendente?.marca && (
                                    <div style={{marginBottom: 8}}>
                                        Detectamos a marca <strong>{melhoriaPendente.marca}</strong>. Os
                                        órgãos publicam esse objeto pelo termo genérico
                                        {melhoriaPendente.termo ? (
                                            <>
                                                {' '}
                                                (<strong>{melhoriaPendente.termo}</strong>)
                                            </>
                                        ) : null}
                                        , o que encontra muito mais preços no PNCP.
                                    </div>
                                )}
                                <div>
                                    Você escreveu: <em>{melhoriaPendente?.original}</em>
                                </div>
                                <div style={{marginTop: 4}}>
                                    Sugerimos pesquisar:{' '}
                                    <strong>{melhoriaPendente?.sugerida}</strong>
                                </div>
                                {melhoriaPendente?.motivo && (
                                    <div style={{marginTop: 8, fontSize: '.82rem', color: '#64748b'}}>
                                        {melhoriaPendente.motivo}
                                    </div>
                                )}
                                <div style={{marginTop: 8, fontSize: '.78rem', color: '#94a3b8'}}>
                                    Ao escolher "Pesquisar como digitei", manteremos sua busca
                                    específica do início ao fim — se precisarmos ampliar, apenas
                                    simplificamos o seu termo, sem trocar o objeto por um genérico.
                                </div>
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    const m = melhoriaPendente
                                    setMelhoriaPendente(null)
                                    // Busca ESTRITA: o usuário rejeitou a generalização —
                                    // o sistema não trocará o objeto por termo genérico.
                                    if (m) doCreate(itemUnico(m.original, undefined, true))
                                }}
                                sx={{textTransform: 'none', color: '#64748b'}}
                            >
                                Pesquisar como digitei
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    const m = melhoriaPendente
                                    setMelhoriaPendente(null)
                                    if (m) {
                                        setDescricao(m.sugerida)
                                        doCreate(itemUnico(m.sugerida, m.marca || undefined))
                                    }
                                }}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    backgroundColor: ACCENT,
                                    '&:hover': {backgroundColor: ACCENT},
                                }}
                            >
                                Pesquisar com a sugestão
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={linhasUnico !== null} onClose={() => setLinhasUnico(null)}>
                        <DialogTitle>Você listou {linhasUnico?.length} itens?</DialogTitle>
                        <DialogContent>
                            <DialogContentText component="div">
                                Esta aba pesquisa <strong>um único item</strong>, mas detectamos{' '}
                                <strong>{linhasUnico?.length} linhas</strong> na descrição — cada linha
                                parece um item diferente. No modo <strong>Vários itens</strong> cada um é
                                pesquisado separadamente (e consome 1 Consulta por item).
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    const ls = linhasUnico
                                    setLinhasUnico(null)
                                    if (ls) {
                                        const desc1 = ls.join(' ')
                                        setDescricao(desc1)
                                        analisarECriar(desc1)
                                    }
                                }}
                                sx={{textTransform: 'none', color: '#64748b'}}
                            >
                                É um item só
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    const ls = linhasUnico
                                    setLinhasUnico(null)
                                    if (ls) {
                                        setListaItens(ls.join('\n'))
                                        setModo('multiplo')
                                    }
                                }}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    backgroundColor: ACCENT,
                                    '&:hover': {backgroundColor: ACCENT},
                                }}
                            >
                                Usar o modo "Vários itens"
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}

            {/* ---------------- LIST ---------------- */}
            {view === 'list' && (
                <Card>
                    <SectionTitle>O que você quer fazer?</SectionTitle>
                    <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                resetForm()
                                setView('form')
                            }}
                            sx={{textTransform: 'none', fontWeight: 700, backgroundColor: ACCENT, '&:hover': {backgroundColor: ACCENT}, px: 2.5, py: 1.3, flex: '1 1 240px'}}
                        >
                            Pesquisa de Preços (Bens e Serviços)
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DescriptionOutlinedIcon />}
                            onClick={() => navigate('/contratacao-direta')}
                            sx={{textTransform: 'none', fontWeight: 700, color: ACCENT, borderColor: ACCENT, px: 2.5, py: 1.3, flex: '1 1 240px'}}
                        >
                            Justificativa de Preços (Contratação Direta)
                        </Button>
                        <Tooltip title="Em desenvolvimento — usará SINAPI/SICRO como referência de custos">
                            <span style={{flex: '1 1 240px', display: 'flex'}}>
                                <Button
                                    variant="outlined"
                                    disabled
                                    sx={{textTransform: 'none', fontWeight: 700, px: 2.5, py: 1.3, width: '100%'}}
                                >
                                    Pesquisa de Preços (Obras e Engenharia) — em desenvolvimento
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                    <Typography variant="caption" sx={{display: 'block', mt: 1.5, color: '#7a8896'}}>
                        Cada item pesquisado consome 1 crédito de Consulta.
                    </Typography>
                </Card>
            )}
            {view === 'list' && (
                <Card>
                    <SectionTitle>Histórico de pesquisas</SectionTitle>
                    <Box sx={{display: 'flex', gap: 1.5, mb: 2, alignItems: 'center'}}>
                        <TextField
                            placeholder="Filtrar por objeto..."
                            value={filtro}
                            onChange={(e) => setFiltro(e.target.value)}
                            size="small"
                            sx={{flex: 1}}
                        />
                        <Tooltip title="Atualizar">
                            <IconButton onClick={loadList}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {loadingList ? (
                        <>
                            <Skeleton height={44} />
                            <Skeleton height={44} />
                            <Skeleton height={44} />
                        </>
                    ) : listError ? (
                        <Alert severity="error">{listError}</Alert>
                    ) : filtrados.length === 0 ? (
                        <Typography variant="body2" sx={{color: '#7a8896', py: 2}}>
                            Nenhuma pesquisa encontrada. Clique em "Nova pesquisa" para começar.
                        </Typography>
                    ) : (
                        <Box sx={{overflowX: 'auto'}}>
                            <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                <thead>
                                    <tr>
                                        <Th>Objeto</Th>
                                        <Th>Modo</Th>
                                        <Th>Itens</Th>
                                        <Th>Status</Th>
                                        <Th>Criada em</Th>
                                        <Th>Feedback</Th>
                                        <Th></Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtrados.map((p) => (
                                        <tr
                                            key={p.id}
                                            onClick={() => selectPesquisa(p.id)}
                                            style={{cursor: 'pointer'}}
                                        >
                                            <Td>
                                                <span style={{color: ACCENT, fontWeight: 600}}>
                                                    {p.objeto_geral || `Pesquisa ${p.id.slice(0, 8)}`}
                                                </span>
                                                {p.origem_modulo && (
                                                    <Chip
                                                        icon={<LinkIcon />}
                                                        label={origemLabel(p.origem_modulo).curto}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            ml: 1,
                                                            height: 22,
                                                            fontSize: 11,
                                                            color: '#5b6b7b',
                                                            '& .MuiChip-icon': {fontSize: 14, color: '#5b6b7b'},
                                                        }}
                                                    />
                                                )}
                                            </Td>
                                            <Td>{modoLabel(p)}</Td>
                                            <Td>{p.itens_total}</Td>
                                            <Td>
                                                <Chip
                                                    label={STATUS_LABEL[p.status] ?? p.status}
                                                    size="small"
                                                    color={statusColor(p.status)}
                                                />
                                            </Td>
                                            <Td>{dateTime(p.created_at)}</Td>
                                            <Td>
                                                <Tooltip title="Enviar feedback sobre esta pesquisa">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(event) => {
                                                            event.stopPropagation()
                                                            handleOpenFeedbackModal(p)
                                                        }}
                                                    >
                                                        <RateReviewOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Td>
                                            <Td>
                                                <Tooltip title="Arquivar pesquisa">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(event) => {
                                                            event.stopPropagation()
                                                            handleOpenArchiveModal(p)
                                                        }}
                                                    >
                                                        <ArchiveOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                    )}
                </Card>
            )}

            {/* ---------------- DETAIL ---------------- */}
            {view === 'detail' &&
                (loadingDetail && !detail ? (
                    <Card>
                        <Skeleton height={32} width="60%" />
                        <Skeleton height={120} />
                    </Card>
                ) : detail && !STATUS_FINAIS.includes(detail.status) ? (
                    <Card>
                        <ProgressoAmigavel detail={detail} />
                    </Card>
                ) : detail ? (
                    <>
                        <Card>
                            <DetailView
                                detail={detail}
                                processing={false}
                                onRefazer={handleRefazer}
                                refazendo={refazendo}
                            />
                            {(() => {
                                const evs = detail.eventos || []
                                const semSaldo = evs.find(
                                    (e) => e.etapa === 'CONSUMO' && e.status === 'SEM_SALDO'
                                )
                                if (semSaldo)
                                    return (
                                        <Alert severity="error" sx={{mt: 2}}>
                                            {semSaldo.detalhe}
                                        </Alert>
                                    )
                                const falha = evs.find(
                                    (e) => e.etapa === 'CONSUMO' && e.status === 'FALHA'
                                )
                                if (falha)
                                    return (
                                        <Alert severity="info" sx={{mt: 2}}>
                                            {falha.detalhe}
                                        </Alert>
                                    )
                                return null
                            })()}
                            {detail.status === 'CONCLUIDO' && (
                                <Box sx={{mt: 2, pt: 2, borderTop: '1px solid #eef1f4'}}>
                                    <Alert severity="warning" sx={{mb: 1.5}}>
                                        Colete e salve as <strong>evidências</strong> (links das fontes) de
                                        forma <strong>contemporânea</strong> a esta pesquisa — com data e hora de
                                        acesso — para juntar ao processo administrativo, caso as utilize (IN 65,
                                        art. 3º e art. 5º, III).
                                    </Alert>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={!!gerando}
                                        startIcon={gerando === 'html' ? <CircularProgress size={14} color="inherit" /> : undefined}
                                        onClick={() => handleExport('html', () => api.verRelatorio(detail.id))}
                                        sx={{textTransform: 'none', backgroundColor: ACCENT, '&:hover': {backgroundColor: ACCENT}}}
                                    >
                                        Ver relatório
                                    </Button>
                                    <Button size="small" variant="outlined" disabled={!!gerando} startIcon={gerando === 'pdf' ? <CircularProgress size={14} /> : undefined} onClick={() => handleExport('pdf', () => api.exportar(detail.id, 'pdf'))} sx={{textTransform: 'none'}}>
                                        Exportar PDF
                                    </Button>
                                    <Button size="small" variant="outlined" disabled={!!gerando} startIcon={gerando === 'xlsx' ? <CircularProgress size={14} /> : undefined} onClick={() => handleExport('xlsx', () => api.exportar(detail.id, 'xlsx'))} sx={{textTransform: 'none'}}>
                                        Excel
                                    </Button>
                                    <Button size="small" variant="outlined" disabled={!!gerando} startIcon={gerando === 'docx' ? <CircularProgress size={14} /> : undefined} onClick={() => handleExport('docx', () => api.exportar(detail.id, 'docx'))} sx={{textTransform: 'none'}}>
                                        Exportar DOCX
                                    </Button>
                                    <Button size="small" variant="outlined" disabled={!!gerando} startIcon={gerando === 'json' ? <CircularProgress size={14} /> : undefined} onClick={() => handleExport('json', () => api.exportar(detail.id, 'json'))} sx={{textTransform: 'none'}}>
                                        Exportar JSON
                                    </Button>
                                    <Button size="small" variant="outlined" disabled={!!gerando} startIcon={gerando === 'cotacao' ? <CircularProgress size={14} /> : undefined} onClick={() => handleExport('cotacao', () => api.solicitacaoCotacao(detail.id))} sx={{textTransform: 'none'}}>
                                        Solicitação de cotação (PDF)
                                    </Button>
                                    </Box>
                                    {gerando && (
                                        <Typography variant="caption" sx={{color: ACCENT, display: 'block', mt: 1, fontWeight: 600}}>
                                            ⏳ Gerando documento… pode levar alguns segundos, aguarde.
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Card>
                        {detail.status === 'CONCLUIDO' && detail.resultado && (
                            <RevisaoCesta
                                detail={detail}
                                api={api}
                                onUpdated={(d) => {
                                    setDetail(d)
                                    setList((prev) =>
                                        prev.map((p) => (p.id === d.id ? {...p, ...d} : p))
                                    )
                                }}
                            />
                        )}
                    </>
                ) : null)}

            <Modal
                open={feedbackModal}
                onClose={handleCloseFeedbackModal}
                aria-labelledby="feedback-modal-title"
                aria-describedby="feedback-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 480,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography
                            id="feedback-modal-title"
                            variant="h6"
                            sx={{fontWeight: 600}}
                        >
                            Enviar feedback
                        </Typography>
                        <IconButton onClick={handleCloseFeedbackModal}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Typography
                        id="feedback-modal-description"
                        sx={{mb: 2, color: '#5b6b7b'}}
                    >
                        {feedbackTarget?.name}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        placeholder="Descreva sua análise sobre esta pesquisa..."
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        sx={{mb: 3}}
                    />
                    <Box sx={{display: 'flex', gap: 2, justifyContent: 'flex-end'}}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseFeedbackModal}
                            sx={{textTransform: 'none'}}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmitFeedback}
                            disabled={!feedbackText.trim() || feedbackSubmitting}
                            sx={{
                                textTransform: 'none',
                                backgroundColor: ACCENT,
                                '&:hover': {backgroundColor: ACCENT},
                            }}
                        >
                            {feedbackSubmitting ? 'Enviando...' : 'Enviar'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal open={!!archiveTarget} onClose={handleCloseArchiveModal}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="h6" component="h2">
                        Confirmar Arquivamento
                    </Typography>
                    <Typography sx={{mt: 2}}>
                        Tem certeza que deseja arquivar a pesquisa{' '}
                        <b style={{color: ACCENT}}>
                            {archiveTarget?.objeto_geral ||
                                `Pesquisa ${archiveTarget?.id.slice(0, 8)}`}
                        </b>
                        ?
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mt: 3,
                        }}
                    >
                        <Button onClick={handleCloseArchiveModal} sx={{mr: 1}}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleConfirmArchive}
                        >
                            Arquivar
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal
                onClose={() => setUnarchieveModal(false)}
                open={unarchieveModal}
                aria-labelledby="unarchive-pesquisas-modal-title"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        maxWidth: 640,
                        maxHeight: '70vh',
                        overflowY: 'auto',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        borderRadius: 2,
                        outline: 'none',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2.5,
                        }}
                    >
                        <Typography
                            id="unarchive-pesquisas-modal-title"
                            variant="h6"
                            component="h2"
                        >
                            Pesquisas arquivadas
                        </Typography>
                        <IconButton onClick={() => setUnarchieveModal(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider orientation="horizontal" flexItem />
                    <Box sx={{p: 2.5}}>
                        <TableContainer component={Box}>
                            <Table sx={{minWidth: 200}} aria-label="pesquisas arquivadas">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Nome</TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {archivedPesquisas?.map((item) => (
                                        <TableRow
                                            key={item.id}
                                            sx={{
                                                '&:last-child td, &:last-child th':
                                                    {border: 0},
                                            }}
                                        >
                                            <TableCell component="th" scope="row">
                                                {item.objeto_geral ||
                                                    `Pesquisa ${item.id.slice(0, 8)}`}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    onClick={() =>
                                                        handleUnarchive(item.id)
                                                    }
                                                >
                                                    <UnarchiveOutlinedIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {archivedPesquisas?.length === 0 && (
                            <Typography variant="body2" sx={{color: '#7a8896', py: 2}}>
                                Nenhuma pesquisa arquivada.
                            </Typography>
                        )}
                        <Box sx={{display: 'flex', justifyContent: 'center', pt: 1}}>
                            <IconButton
                                disabled={archivedPesquisasPage === 1}
                                onClick={() =>
                                    goToArchivedPesquisasPage(
                                        archivedPesquisasPage - 1
                                    )
                                }
                            >
                                <KeyboardArrowLeftIcon />
                            </IconButton>
                            <IconButton
                                disabled={(archivedPesquisas?.length || 0) < 15}
                                onClick={() =>
                                    goToArchivedPesquisasPage(
                                        archivedPesquisasPage + 1
                                    )
                                }
                            >
                                <KeyboardArrowRightIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={feedbackToast.open}
                autoHideDuration={4000}
                onClose={() =>
                    setFeedbackToast((prev) => ({...prev, open: false}))
                }
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert
                    severity={feedbackToast.severity}
                    onClose={() =>
                        setFeedbackToast((prev) => ({...prev, open: false}))
                    }
                >
                    {feedbackToast.message}
                </Alert>
            </Snackbar>
            <AiDisclaimer text="Esta pesquisa de preços é gerada por inteligência artificial a partir de fontes públicas e pode conter valores imprecisos ou desatualizados. Confira os dados antes de utilizá-los." />
        </Container>
    )
}

// ---------------- Detalhe ----------------
function DetailView({
    detail,
    processing,
    onRefazer,
    refazendo,
}: {
    detail: PriceSearchDetail
    processing: boolean
    onRefazer?: () => void
    refazendo?: boolean
}) {
    // Navegação reversa do vínculo de origem (mini-contrato entre módulos): o
    // CaronaManager lê {origem:'pesquisa', justificativa_id} do state e abre a
    // justificativa correspondente.
    const navigate = useNavigate()
    const [filtroItens, setFiltroItens] = useState('')
    const resultado = detail.resultado || {}
    const itensAnalise: any[] =
        (resultado.analise_por_item as any[]) || (resultado.itens as any[]) || []
    const itensFiltrados = useMemo(() => {
        const termo = filtroItens.trim().toLowerCase()
        if (!termo) return itensAnalise
        return itensAnalise.filter((it) => {
            const desc = it.item?.descricao || it.descricao || ''
            return desc.toLowerCase().includes(termo)
        })
    }, [itensAnalise, filtroItens])
    const valorGlobal =
        resultado.valor_total_estimado_global ?? detail.valor_total_estimado ?? null
    const progresso =
        detail.itens_total > 0
            ? Math.round((detail.itens_concluidos / detail.itens_total) * 100)
            : 0

    return (
        <Box>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0}}>
                    <Typography variant="h6" sx={{fontWeight: 600}}>
                        {detail.objeto_geral || 'Pesquisa de preços'}
                    </Typography>
                    {detail.origem_modulo && (
                        <Chip
                            icon={<LinkIcon />}
                            label={origemLabel(detail.origem_modulo).longo}
                            size="small"
                            variant="outlined"
                            sx={{
                                fontWeight: 700,
                                color: ACCENT,
                                borderColor: ACCENT,
                                '& .MuiChip-icon': {color: ACCENT},
                            }}
                            // Só o vínculo com o Ata Inteligente tem navegação
                            // reversa contratada; outros módulos ficam como tag
                            // informativa até definirem o próprio leitor de state.
                            {...(detail.origem_modulo === 'carona' &&
                            detail.origem_ref_id
                                ? {
                                      clickable: true,
                                      onClick: () =>
                                          navigate('/ata-inteligente', {
                                              state: {
                                                  origem: 'pesquisa',
                                                  justificativa_id:
                                                      detail.origem_ref_id,
                                              },
                                          }),
                                  }
                                : {})}
                        />
                    )}
                </Box>
                <Chip label={STATUS_LABEL[detail.status] ?? detail.status} color={statusColor(detail.status)} />
            </Box>

            {processing && (
                <Box sx={{mb: 2}}>
                    <Typography variant="caption" sx={{color: '#7a8896'}}>
                        Processando — {detail.itens_concluidos}/{detail.itens_total} itens
                    </Typography>
                    <LinearProgress
                        variant={progresso > 0 ? 'determinate' : 'indeterminate'}
                        value={progresso}
                        sx={{borderRadius: 1, mt: 0.5}}
                    />
                </Box>
            )}

            {detail.status === 'ERRO' && (
                <Alert
                    severity="error"
                    sx={{mb: 2}}
                    action={
                        onRefazer && (
                            <Button
                                size="small"
                                color="inherit"
                                startIcon={
                                    refazendo ? (
                                        <CircularProgress size={14} color="inherit" />
                                    ) : (
                                        <RefreshIcon fontSize="small" />
                                    )
                                }
                                disabled={refazendo}
                                onClick={onRefazer}
                            >
                                {refazendo ? 'Refazendo…' : 'Refazer'}
                            </Button>
                        )
                    }
                >
                    {detail.error_message || 'A pesquisa terminou com erro.'}
                </Alert>
            )}

            {valorGlobal != null && (
                <Card style={{backgroundColor: '#f0f7ff', marginBottom: 16}}>
                    <Typography variant="caption" sx={{color: '#5b6b7b'}}>
                        Valor total estimado (referência)
                    </Typography>
                    <Typography variant="h5" sx={{fontWeight: 700}}>
                        {currency(valorGlobal)}
                    </Typography>
                </Card>
            )}

            {itensAnalise.length > 0 && (
                <>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                        <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                            Itens ({itensFiltrados.length}/{itensAnalise.length})
                        </Typography>
                    </Box>
                    {itensAnalise.length > 5 && (
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Buscar item pela descrição..."
                            value={filtroItens}
                            onChange={(e) => setFiltroItens(e.target.value)}
                            sx={{mb: 1.5}}
                        />
                    )}
                    {itensFiltrados.length === 0 && (
                        <Typography variant="body2" sx={{color: '#7a8896', mb: 1}}>
                            Nenhum item encontrado para "{filtroItens}".
                        </Typography>
                    )}
                    {itensFiltrados.map((it, i) => {
                        const est = it.estatistica || it.analise || {}
                        const desc = it.item?.descricao || it.descricao || `Item ${i + 1}`
                        return (
                            <Accordion
                                key={i}
                                defaultExpanded={itensFiltrados.length <= 5 || i === 0}
                                disableGutters
                                elevation={0}
                                square
                                sx={{
                                    border: '1px solid #ececec',
                                    borderRadius: '12px !important',
                                    mb: 1,
                                    '&:before': {display: 'none'},
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{px: 1.5, '& .MuiAccordionSummary-content': {my: 1}}}
                                >
                                    <Typography variant="body2" sx={{fontWeight: 600}}>
                                        {desc}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{px: 1.5, pt: 0}}>
                                    <Box sx={{display: 'flex', gap: 3, flexWrap: 'wrap', mt: 0.5}}>
                                        <Metric label="Mediana" value={currency(est.mediana)} />
                                        <Metric label="Média" value={currency(est.media)} />
                                        <Metric
                                            label="Valor estimado"
                                            value={currency(it.valor_total_estimado ?? it.valor_estimado)}
                                        />
                                        {est.cv != null && (
                                            <Metric label="CV" value={`${Number(est.cv).toFixed(1)}%`} />
                                        )}
                                    </Box>
                                    {it.cesta_suficiente === false && (
                                        <Alert severity="warning" sx={{mt: 1}}>
                                            Cesta de preços insuficiente para este item — considere ampliar o
                                            período ou as fontes.
                                        </Alert>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        )
                    })}
                </>
            )}

            {detail.eventos && detail.eventos.length > 0 && (
                <>
                    <Divider sx={{my: 2}} />
                    <Typography variant="subtitle2" sx={{fontWeight: 700, mb: 0.5}}>
                        Auditoria
                    </Typography>
                    <Typography variant="caption" sx={{color: '#7a8896', display: 'block', mb: 1}}>
                        Trilha de auditoria da pesquisa (art. 3º da IN SEGES/ME nº 65/2021).
                    </Typography>
                    {detail.eventos
                        .slice()
                        .reverse()
                        .map((ev: EventoOut, i: number) => (
                            <Box key={i} sx={{display: 'flex', gap: 1, mb: 0.5}}>
                                <Typography variant="caption" sx={{color: '#7a8896', minWidth: 130}}>
                                    {dateTime(ev.timestamp)}
                                </Typography>
                                <Typography variant="caption">
                                    <strong>{eventoTitulo(ev.etapa)}</strong>
                                    {eventoDescricao(ev) ? ` — ${eventoDescricao(ev)}` : ''}
                                </Typography>
                            </Box>
                        ))}
                </>
            )}
        </Box>
    )
}

function Metric({label, value}: {label: string; value: string}) {
    return (
        <Box>
            <Typography variant="caption" sx={{color: '#7a8896'}}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{fontWeight: 600}}>
                {value}
            </Typography>
        </Box>
    )
}

// ---------------- Progresso amigável (com dicas) ----------------
// Dicas "Você sabia?" baseadas na IN SEGES/ME nº 65/2021 (texto oficial).
const DICAS = [
    'A IN SEGES/ME nº 65/2021 é a norma que rege o procedimento de pesquisa de preços para compras e serviços em geral (Lei 14.133/2021).',
    'A IN 65 NÃO se aplica a obras e serviços de engenharia — esses têm regras próprias (SINAPI/SICRO).',
    'Estados e municípios devem seguir a IN 65 quando executam recursos da União por transferências voluntárias.',
    'A IN 65 também vale para aferir a vantagem econômica de adesões a atas de registro de preços (carona).',
    '"Preço estimado" é o valor obtido por método matemático sobre os preços coletados, desconsiderando os inexequíveis, inconsistentes e excessivamente elevados (art. 2º, I).',
    '"Sobrepreço" é o preço orçado/contratado muito acima dos preços de mercado — o que a pesquisa busca evitar.',
    'O documento de pesquisa deve conter, no mínimo, a descrição do objeto e a identificação do responsável (art. 3º, I e II).',
    'A pesquisa precisa caracterizar as fontes consultadas e registrar a série de preços coletados (art. 3º, III e IV).',
    'O documento deve trazer o método estatístico aplicado e a justificativa da metodologia (art. 3º, V e VI).',
    'É exigida memória de cálculo do valor estimado e os documentos que a suportam (art. 3º, VII).',
    'No Parâmetro IV, é preciso justificar a escolha dos fornecedores consultados (art. 3º, VIII).',
    'Sempre que possível, considere prazos e locais de entrega, quantidade, pagamento, frete, garantias e marca/modelo (art. 4º).',
    'O art. 5º traz 5 parâmetros de pesquisa, que podem ser usados de forma combinada ou não.',
    'Parâmetro I: composição de custos ≤ mediana dos sistemas oficiais (Painel de Preços, Banco de Preços em Saúde).',
    'Parâmetro II: contratações similares da Administração no período de até 1 ano (inclui registro de preços).',
    'Parâmetro III: mídia especializada e sítios eletrônicos, atualizados e em até 6 meses antes do edital.',
    'Parâmetro IV: pesquisa direta com no mínimo 3 fornecedores, por solicitação formal de cotação.',
    'Parâmetro V: base nacional de notas fiscais eletrônicas, com notas de até 1 ano antes do edital.',
    'A IN 65 manda PRIORIZAR os Parâmetros I e II; usar os demais exige justificativa nos autos (art. 5º, §1º).',
    'No Parâmetro IV, a proposta do fornecedor deve trazer descrição, valor unitário e total, CNPJ/CPF e contato (art. 5º, §2º).',
    'A proposta de cotação também precisa de data de emissão e identificação do responsável (art. 5º, §2º, II).',
    'Registre nos autos os fornecedores consultados que NÃO responderam à solicitação de cotação (art. 5º, §2º, IV).',
    'O valor de referência pode ser a MÉDIA, a MEDIANA ou o MENOR dos preços coletados (art. 6º).',
    'O cálculo do art. 6º exige um conjunto de 3 ou mais preços para ser válido.',
    'Devem ser desconsiderados os valores inexequíveis, inconsistentes e excessivamente elevados (art. 6º).',
    'Outros critérios além de média/mediana/menor são possíveis, se justificados e aprovados pela autoridade (art. 6º, §1º).',
    'Excepcionalmente, admite-se preço estimado com base em menos de 3 preços, se justificado nos autos (art. 6º, §5º).',
    'Quando o preço se basear só no Parâmetro I, ele não pode superar a mediana dos sistemas consultados (art. 6º, §6º).',
    'Os preços coletados devem ser analisados de forma crítica, sobretudo quando há grande variação entre eles (art. 6º, §4º).',
    'Por isso usamos a mediana quando a dispersão é alta (coeficiente de variação acima de 25%).',
    'Preços muito acima da mediana costumam ser valor de LOTE/CAIXA, não unitário — por isso são filtrados.',
    'O "teste de outlier" mantém os preços na faixa de 70% a 130% em torno da referência da cesta.',
    'A equalização padroniza tempo, unidade de medida e embalagem antes de comparar os preços.',
    'Priorizamos preços HOMOLOGADOS no PNCP (efetivamente adjudicados), não o orçamento estimado do edital.',
    'Você pode revisar a cesta: incluir, excluir ou editar preços e escolher o método antes de fechar.',
    'Na contratação direta (art. 7º), aplicam-se os mesmos parâmetros do art. 5º.',
    'Na inexigibilidade, se não der para usar o art. 5º, a justificativa de preço usa notas fiscais do próprio fornecedor (art. 7º, §1º).',
    'É vedada a contratação direta por inexigibilidade quando a justificativa de preços revela que havia competição (art. 7º, §3º).',
    'Itens de TIC têm catálogos oficiais cujos preços servem de teto, salvo pesquisa com valor menor (art. 8º).',
    'O orçamento estimado pode ser sigiloso, se justificado — exceto quando o julgamento for por maior desconto (art. 10).',
]
const PASSOS = [
    {label: 'Na fila', icone: '⏳'},
    {label: 'Entendendo o que você pediu', icone: '📝'},
    {label: 'Buscando preços no PNCP e no varejo', icone: '🔎'},
    {label: 'Analisando e equalizando os preços', icone: '⚖️'},
    {label: 'Montando seu relatório', icone: '📄'},
    {label: 'Pronto!', icone: '🎉'},
]
function stepIndex(status: StatusPesquisa): number {
    switch (status) {
        case 'PENDENTE':
            return 0
        case 'PARSEANDO':
            return 1
        case 'COLETANDO':
            return 2
        case 'AGUARDANDO_LLM':
        case 'CALCULANDO':
            return 3
        case 'GERANDO_RELATORIO':
            return 4
        case 'CONCLUIDO':
            return 5
        default:
            return 0
    }
}

function ProgressoAmigavel({detail}: {detail: PriceSearchDetail}) {
    const atual = stepIndex(detail.status)
    const [dica, setDica] = useState(0)
    const [mostrarTodos, setMostrarTodos] = useState(false)
    useEffect(() => {
        const t = setInterval(() => setDica((d) => (d + 1) % DICAS.length), 5000)
        return () => clearInterval(t)
    }, [])
    return (
        <Box>
            <Typography variant="h6" sx={{fontWeight: 700, mb: 0.5}}>
                Estamos preparando sua pesquisa de preços…
            </Typography>
            <Typography variant="body2" sx={{color: '#5b6b7b', mb: 2}}>
                Leva cerca de um minuto. Pode relaxar que a gente cuida de tudo. ☕
            </Typography>
            {PASSOS.map((p, i) => {
                const feito = i < atual
                const agora = i === atual
                return (
                    <Box
                        key={i}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 0.8,
                            opacity: i <= atual ? 1 : 0.45,
                        }}
                    >
                        <Box sx={{width: 26, display: 'flex', justifyContent: 'center'}}>
                            {feito ? (
                                <span style={{color: '#1d9e75', fontSize: 18}}>✓</span>
                            ) : agora ? (
                                <CircularProgress size={16} />
                            ) : (
                                <span style={{color: '#cbd5e1'}}>○</span>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{fontWeight: agora ? 700 : 500}}>
                            {p.icone} {p.label}
                        </Typography>
                    </Box>
                )
            })}
            {detail.eventos && detail.eventos.length > 0 && (
                <Box
                    sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: '12px',
                        background: '#f8fafc',
                        border: '1px solid #eef1f4',
                    }}
                >
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5}}>
                        <Typography variant="caption" sx={{color: '#7a8896', fontWeight: 700}}>
                            O que está acontecendo agora
                        </Typography>
                        {detail.eventos.length > 5 && (
                            <Button
                                size="small"
                                onClick={() => setMostrarTodos((v) => !v)}
                                sx={{textTransform: 'none', fontSize: '.72rem', color: ACCENT, p: 0, minWidth: 0}}
                            >
                                {mostrarTodos
                                    ? 'Mostrar só os recentes'
                                    : `Mostrar tudo (${detail.eventos.length})`}
                            </Button>
                        )}
                    </Box>
                    <Box sx={mostrarTodos ? {maxHeight: 220, overflowY: 'auto', pr: 0.5} : undefined}>
                        {(mostrarTodos ? detail.eventos.slice() : detail.eventos.slice(-5))
                            .reverse()
                            .map((ev, i) => (
                                <Typography key={i} variant="caption" sx={{display: 'block', color: '#334155'}}>
                                    <span style={{color: '#94a3b8', fontVariantNumeric: 'tabular-nums'}}>
                                        {horaExata(ev.timestamp)}
                                    </span>{' '}
                                    • <strong>{eventoTitulo(ev.etapa)}</strong>
                                    {eventoDescricao(ev) ? ` — ${eventoDescricao(ev)}` : ''}
                                </Typography>
                            ))}
                    </Box>
                </Box>
            )}
            <Box
                sx={{
                    mt: 2.5,
                    p: 2,
                    borderRadius: '12px',
                    background: '#eef5ff',
                    borderLeft: `4px solid ${ACCENT}`,
                }}
            >
                <Typography variant="caption" sx={{color: ACCENT, fontWeight: 700, display: 'block'}}>
                    💡 Você sabia?
                </Typography>
                <Typography variant="body2" sx={{color: '#334155'}}>
                    {DICAS[dica]}
                </Typography>
            </Box>
        </Box>
    )
}

// ---------------- Revisão da cesta (Art. 6º / Parâmetro IV) ----------------
type LinhaRev = {
    id_coleta: number
    usar: boolean
    valor: string // valor EDITÁVEL da cesta (equalizado, com fallback pro bruto)
    origem: string
    param: string
    fonte: string
    desc: string
    link?: string
    situacao: string // rótulo curto: por que a linha está (ou não) na cesta
    naCesta: boolean
    // --- transparência da equalização (campos podem faltar por corrida de deploy) ---
    motivoDescarte?: string // código cru (p/ particionar "base não confirmada")
    valorOrigem?: number | null // valor bruto de origem
    unidadeOrigem?: string // unidade de origem (ex.: "DÚZIA")
    unidadeBase?: string // unidade-base de comparação (ex.: "un")
    fator?: number | null // fator de embalagem aplicado
    confiabilidade?: string // ALTA/MEDIA/BAIXA
    baseConfirmada?: boolean // false = base assumida sem confirmação
    foiEqualizado: boolean // origem→base efetivamente convertida
    baseAplicada?: string // prosa pronta p/ tooltip
    premissas?: string[]
    riscos?: string[]
    // Presente só em preço de origem DOCUMENTO — arquivo, página e trecho literal de
    // onde o número saiu. É o que sustenta o rótulo de procedência na revisão.
    proveniencia?: PrecoResultado['proveniencia']
}

// Banner por item: unidade-base de comparação × unidade majoritária de venda.
type BannerUnidades = {
    unidadeBase?: string // "Comparado por ___"
    unidadeDominante: string // "vendido majoritariamente por ___"
    n: number // contratações na unidade dominante
    m: number // total de contratações consideradas
}

// Rótulos curtos para a situação de cada preço na revisão (o "porquê" da linha).
const MOTIVO_CURTO: Record<string, string> = {
    NAO_PERTINENTE: 'Não pertinente ao objeto',
    ESPECIFICACAO_INCOMPATIVEL: 'Especificação incompatível',
    EQUALIZACAO_INSUFICIENTE: 'Não foi possível padronizar',
    // Guardrail fail-safe da equalização: o motor descarta quando NÃO há evidência
    // positiva da quantidade vendável (BASE_NAO_CONFIRMADA) e marca ambiguidade
    // quando há mais de uma leitura plausível da embalagem (BASE_AMBIGUA — em
    // princípio transitório, mas rotulado aqui para nunca cair no genérico
    // "Descartado", que esconde do usuário justamente o motivo que ele precisa ver).
    BASE_NAO_CONFIRMADA: 'Base do preço não confirmada',
    BASE_AMBIGUA: 'Base ambígua — conferir',
    ORCAMENTO_SIGILOSO: 'Orçamento sigiloso',
    FORA_PERIODO_VALIDO: 'Fora do período',
    DADOS_INCOMPLETOS: 'Dados incompletos',
    OUTLIER_ART6: 'Fora da faixa (Art. 6º)',
    FORA_DA_CESTA: 'Coletado além da cesta automática',
    // Excedente que FOI à análise e voltou sem juízo (lote falhou / resposta não
    // cobriu o preço). Rótulo separado do irmão acima porque a causa é outra:
    // ali a regra de volume da cesta, aqui indisponibilidade técnica. Dizer
    // "além da cesta" quando a IA não respondeu alega critério onde houve falha —
    // e o gestor precisa dessa diferença para decidir se tica o preço na revisão.
    ANALISE_INDISPONIVEL: 'Análise não retornou para este preço',
    FORA_DA_UF: 'Fora da UF selecionada',
    // Preço lido de PDF enviado pelo responsável. NÃO é defeito do preço — é a
    // condição dele: a fonte não foi consultada por esta pesquisa, então quem o
    // coloca na cesta é o gestor, assumindo a autoria da inclusão.
    FONTE_DOCUMENTAL: 'Do seu documento — inclua se quiser usar',
    AGREGADO_NAO_E_PRECO: 'Média/total do documento, não é preço unitário',
}
type ItemRev = {
    numero: number
    descricao: string
    metodo: string
    metodoJustificativa: string
    novoFornecedor: string
    novoValor: string
    linhas: LinhaRev[]
    banner?: BannerUnidades // unidade dominante × unidade-base (undefined = nada a explicar)
}

// Uma linha é "base não confirmada" quando a padronização foi assumida sem
// confirmação (base_confirmada === false) ou descartada por falha de equalização.
function isBaseNaoConfirmada(l: LinhaRev): boolean {
    return (
        l.baseConfirmada === false ||
        l.motivoDescarte === 'EQUALIZACAO_INSUFICIENTE' ||
        l.motivoDescarte === 'BASE_NAO_CONFIRMADA'
    )
}

// Deriva o banner do item: por qual unidade-base comparamos e qual unidade de venda
// predomina (N de M).
//
// "Vendido majoritariamente por X (N de M contratações)" é uma AFIRMAÇÃO FACTUAL
// sobre o mercado, então só pode sair da estatística que o motor calculou sobre a
// cesta final (estatistica.unidade_dominante + distribuicao_unidades, gravadas
// juntas). O front NÃO reconta nem vota: recontar a moda das unidades de origem das
// linhas da tela produzia um "majoritariamente" que ninguém apurou (e um "N de M"
// que não fecha com a cesta). Sem o dado do backend, o banner é OMITIDO — falha
// segura: melhor não explicar do que explicar errado.
function montarBanner(a: any, linhas: LinhaRev[]): BannerUnidades | undefined {
    const est = a?.estatistica || a?.analise || {}
    const unidadeDominante =
        typeof est.unidade_dominante === 'string' ? est.unidade_dominante.trim() : ''
    if (!unidadeDominante) return undefined

    const distrib: Record<string, number> = {}
    const raw = est.distribuicao_unidades
    if (raw && typeof raw === 'object') {
        for (const [k, v] of Object.entries(raw)) {
            const n = Number(v)
            if (k && Number.isFinite(n) && n > 0) distrib[k] = n
        }
    }
    const m = Object.values(distrib).reduce((s, n) => s + n, 0)

    // Unidade-base de comparação: é uma propriedade ÚNICA do motor para o item (não
    // uma estatística), então basta LER — do resumo do item ou dos preços da cesta
    // quando todos apontam a mesma. Divergência entre linhas = não afirmamos nada.
    const basesUsadas = new Set(
        linhas
            .filter((l) => l.usar)
            .map((l) => (l.unidadeBase || '').trim())
            .filter(Boolean)
    )
    const unidadeBase =
        (typeof est.unidade_base === 'string' && est.unidade_base.trim()) ||
        (basesUsadas.size === 1 ? Array.from(basesUsadas)[0] : undefined)

    if (m < 1) return undefined
    // Só vale a pena mostrar quando há algo a esclarecer: mais de uma unidade de venda,
    // alguma equalização, ou base de comparação diferente da unidade dominante.
    const relevante =
        Object.keys(distrib).length > 1 ||
        linhas.some((l) => l.foiEqualizado) ||
        (!!unidadeBase && unidadeBase !== unidadeDominante)
    if (!relevante) return undefined

    const n = distrib[unidadeDominante] ?? 0
    return {unidadeBase, unidadeDominante, n, m}
}

// Rótulo da coluna "Fonte". Preço de DOCUMENTO não pode cair no default "Web": ele
// não veio da web, veio de um PDF que o próprio responsável enviou, e apresentá-lo
// como coleta nossa é exatamente a confusão que a procedência existe para evitar.
function fonteDoPreco(pr: PrecoResultado): string {
    if (pr.origem === 'PNCP') return pr.orgao_contratante || 'PNCP'
    if (pr.origem === 'DOCUMENTO') {
        const prov = pr.proveniencia || {}
        const arq = prov.arquivo_nome || 'documento enviado'
        return prov.pagina ? `${arq} (p. ${prov.pagina})` : arq
    }
    return pr.fornecedor_nome || 'Web'
}

function montarLinha(p: any): LinhaRev {
    const pr = p as PrecoResultado
    const usado = pr.status_utilizacao === 'UTILIZADO'
    // Preço zerado/ausente = a padronização não derivou valor (ex.: peça ou
    // serviço relacionado, não o objeto). Cai no valor BRUTO coletado como
    // ponto de partida editável — nunca um "0" mudo na tela.
    const eq = Number(pr.valor_unitario_equalizado)
    const bruto = Number(pr.valor_unitario)
    const valorBase = Number.isFinite(eq) && eq > 0 ? eq : Number.isFinite(bruto) && bruto > 0 ? bruto : null
    const eqz = pr.equalizacao || {}
    const fator = eqz.fator_embalagem != null ? Number(eqz.fator_embalagem) : null
    // foi_equalizado é novo; se ausente por corrida de deploy, derivamos do fator ou
    // da diferença bruto↔equalizado.
    const foiEqualizado =
        eqz.foi_equalizado === true ||
        (eqz.foi_equalizado == null &&
            ((fator != null && fator > 1) ||
                (Number.isFinite(eq) && Number.isFinite(bruto) && eq > 0 && eq !== bruto)))
    return {
        id_coleta: pr.id_coleta,
        usar: usado,
        valor: valorBase != null ? String(valorBase) : '',
        origem: pr.origem || '',
        param: pr.parametro_in65 || '',
        fonte: fonteDoPreco(pr),
        proveniencia: pr.proveniencia || undefined,
        desc: pr.descricao_item_encontrado || '',
        link: pr.link || undefined,
        naCesta: usado,
        situacao: usado ? 'Na cesta' : MOTIVO_CURTO[pr.motivo_descarte || ''] || 'Descartado',
        motivoDescarte: pr.motivo_descarte || undefined,
        valorOrigem: Number.isFinite(bruto) && bruto > 0 ? bruto : null,
        unidadeOrigem: pr.unidade || undefined,
        unidadeBase: eqz.unidade_base || undefined,
        fator,
        confiabilidade: eqz.confiabilidade || undefined,
        baseConfirmada: eqz.base_confirmada == null ? undefined : eqz.base_confirmada,
        foiEqualizado,
        baseAplicada: eqz.base_aplicada || undefined,
        premissas: eqz.premissas || undefined,
        riscos: eqz.riscos || undefined,
    }
}

function montarItens(detail: PriceSearchDetail): ItemRev[] {
    const itens = (detail.resultado?.analise_por_item as any[]) || []
    return itens.map((a: any) => {
        const linhas: LinhaRev[] = ((a.precos as any[]) || []).map(montarLinha)
        return {
            numero: a.item?.numero,
            descricao: a.item?.descricao || `Item ${a.item?.numero}`,
            metodo: 'AUTO',
            metodoJustificativa: '',
            novoFornecedor: '',
            novoValor: '',
            linhas,
            banner: montarBanner(a, linhas),
        }
    })
}

// Conteúdo do tooltip do selo "equalizado": como o valor foi padronizado + riscos.
function TooltipEqualizacao({l}: {l: LinhaRev}) {
    const temTexto = !!l.baseAplicada || !!l.premissas?.length || !!l.riscos?.length
    return (
        <Box sx={{maxWidth: 260}}>
            {l.baseAplicada && (
                <Typography variant="caption" sx={{display: 'block', fontWeight: 700, mb: 0.5}}>
                    {l.baseAplicada}
                </Typography>
            )}
            {l.fator != null && l.fator > 1 && (
                <Typography variant="caption" sx={{display: 'block'}}>
                    Fator de embalagem: {l.fator}
                </Typography>
            )}
            {l.confiabilidade && (
                <Typography variant="caption" sx={{display: 'block'}}>
                    Confiabilidade: {l.confiabilidade}
                </Typography>
            )}
            {(l.premissas || []).map((p, i) => (
                <Typography key={`p${i}`} variant="caption" sx={{display: 'block'}}>
                    • {p}
                </Typography>
            ))}
            {(l.riscos || []).map((r, i) => (
                <Typography key={`r${i}`} variant="caption" sx={{display: 'block', color: '#ffcc80'}}>
                    ⚠ {r}
                </Typography>
            ))}
            {!temTexto && (
                <Typography variant="caption">
                    Valor padronizado para a unidade-base de comparação.
                </Typography>
            )}
        </Box>
    )
}

// Célula "Valor unit.": selo origem→equalizado (quando houve equalização) + campo
// editável. O valor da cesta continua sendo o equalizado (não quebra o revisar()).
function ValorUnitCell({l, onChange}: {l: LinhaRev; onChange: (v: string) => void}) {
    const unidadeExibida = l.unidadeBase || l.unidadeOrigem || ''
    // Selo colorido pela confiabilidade da equalização (sinal acessível de risco).
    const corSelo: 'success' | 'warning' | 'info' =
        l.confiabilidade === 'BAIXA' ? 'warning' : l.confiabilidade === 'ALTA' ? 'success' : 'info'
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 150}}>
            {l.foiEqualizado ? (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap'}}>
                    <Typography component="span" variant="caption" sx={{color: '#7a8896'}}>
                        {currency(l.valorOrigem)}
                        {l.unidadeOrigem ? ` /${l.unidadeOrigem}` : ''}
                    </Typography>
                    <span aria-hidden="true" style={{color: '#7a8896'}}>
                        →
                    </span>
                    <Typography component="span" variant="caption" sx={{color: '#2a3340', fontWeight: 700}}>
                        {currency(Number(l.valor))}
                        {l.unidadeBase ? ` /${l.unidadeBase}` : ''}
                    </Typography>
                    <Tooltip title={<TooltipEqualizacao l={l} />} arrow>
                        <Chip
                            size="small"
                            label="equalizado"
                            color={corSelo}
                            variant="outlined"
                            aria-label={`Preço equalizado${l.baseAplicada ? `: ${l.baseAplicada}` : ''}`}
                            sx={{height: 18, fontSize: '.6rem', cursor: 'help'}}
                        />
                    </Tooltip>
                </Box>
            ) : (
                unidadeExibida && (
                    <Typography component="span" variant="caption" sx={{color: '#7a8896'}}>
                        por {unidadeExibida}
                    </Typography>
                )
            )}
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <TextField
                    size="small"
                    type="number"
                    value={l.valor}
                    onChange={(e) => onChange(e.target.value)}
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    sx={{width: 110}}
                    inputProps={{'aria-label': `Valor unitário${unidadeExibida ? ` por ${unidadeExibida}` : ''}`}}
                />
                {unidadeExibida && (
                    <Typography component="span" variant="caption" sx={{color: '#7a8896'}}>
                        /{unidadeExibida}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

// Uma linha (<tr>) da tabela de revisão. Extraída p/ reuso entre a tabela principal
// e a seção "base não confirmada".
function LinhaRevRow({
    l,
    ii,
    li,
    patchLinha,
}: {
    l: LinhaRev
    ii: number
    li: number
    patchLinha: (ii: number, li: number, patch: Partial<LinhaRev>) => void
}) {
    return (
        <tr style={{opacity: l.usar ? 1 : 0.5}}>
            <Td>
                <Checkbox
                    size="small"
                    checked={l.usar}
                    onChange={(e) => patchLinha(ii, li, {usar: e.target.checked})}
                    inputProps={{'aria-label': `Usar preço de ${l.fonte} na cesta`}}
                />
            </Td>
            <Td>
                <Chip
                    size="small"
                    label={l.situacao}
                    color={
                        l.naCesta
                            ? 'success'
                            : l.situacao === 'Coletado além da cesta automática' ||
                                l.situacao === 'Análise não retornou para este preço'
                              ? 'default'
                              : 'warning'
                    }
                    variant={l.naCesta ? 'filled' : 'outlined'}
                    sx={{fontSize: '.68rem', maxWidth: 170}}
                />
            </Td>
            <Td>
                <Chip
                    size="small"
                    label={
                        l.origem === 'DOCUMENTO'
                            ? `Seu documento${l.param ? ` ${l.param}` : ''}`
                            : `${l.origem} ${l.param}`
                    }
                    color={l.origem === 'DOCUMENTO' ? 'info' : 'default'}
                    variant={l.origem === 'DOCUMENTO' ? 'outlined' : 'filled'}
                />
            </Td>
            <Td>{l.fonte}</Td>
            <Td>
                <span style={{display: 'inline-block', maxWidth: 320, fontSize: 13}}>
                    {l.desc.slice(0, 160)}
                </span>
            </Td>
            <Td>
                {l.link ? (
                    <a href={l.link} target="_blank" rel="noopener noreferrer" style={{color: ACCENT}}>
                        abrir
                    </a>
                ) : l.proveniencia?.trecho_literal ? (
                    // Preço de documento não tem URL. Um "—" aqui apagaria a única
                    // evidência que ele tem — o trecho literal do PDF, que é o que o
                    // responsável precisa conferir antes de ticar a inclusão.
                    <Tooltip
                        title={
                            <span style={{whiteSpace: 'pre-wrap'}}>
                                {l.proveniencia.trecho_literal}
                                {l.proveniencia.fonte_declarada
                                    ? `\n\nFonte declarada no documento: ${l.proveniencia.fonte_declarada}`
                                    : ''}
                            </span>
                        }
                    >
                        <span style={{color: ACCENT, cursor: 'help', fontSize: 13}}>
                            ver trecho
                        </span>
                    </Tooltip>
                ) : (
                    '—'
                )}
            </Td>
            <Td>
                <ValorUnitCell l={l} onChange={(v) => patchLinha(ii, li, {valor: v})} />
            </Td>
        </tr>
    )
}

// Tabela de revisão (cabeçalho + linhas). Recebe as linhas já com o índice original
// (li) para que patchLinha continue apontando para it.linhas[li].
function TabelaRevisao({
    linhas,
    ii,
    patchLinha,
}: {
    linhas: Array<{l: LinhaRev; li: number}>
    ii: number
    patchLinha: (ii: number, li: number, patch: Partial<LinhaRev>) => void
}) {
    return (
        <Box sx={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                    <tr>
                        <Th>Usar</Th>
                        <Th>Situação</Th>
                        <Th>Origem</Th>
                        <Th>Fonte</Th>
                        <Th>Descrição encontrada</Th>
                        <Th>Link</Th>
                        <Th>Valor unit.</Th>
                    </tr>
                </thead>
                <tbody>
                    {linhas.map(({l, li}) => (
                        <LinhaRevRow key={l.id_coleta} l={l} ii={ii} li={li} patchLinha={patchLinha} />
                    ))}
                </tbody>
            </table>
        </Box>
    )
}

function RevisaoCesta({
    detail,
    api,
    onUpdated,
}: {
    detail: PriceSearchDetail
    api: PriceResearchApiService
    onUpdated: (d: PriceSearchDetail) => void
}) {
    const [itens, setItens] = useState<ItemRev[]>(() => montarItens(detail))
    const [salvando, setSalvando] = useState(false)
    useEffect(() => {
        setItens(montarItens(detail))
    }, [detail])

    const patchLinha = (ii: number, li: number, patch: Partial<LinhaRev>) =>
        setItens((prev) =>
            prev.map((it, i) =>
                i !== ii
                    ? it
                    : {...it, linhas: it.linhas.map((l, j) => (j !== li ? l : {...l, ...patch}))}
            )
        )
    const patchItem = (ii: number, patch: Partial<ItemRev>) =>
        setItens((prev) => prev.map((it, i) => (i !== ii ? it : {...it, ...patch})))

    const salvar = async () => {
        const ajustes: any[] = []
        const novos: any[] = []
        const metodos: any[] = []
        for (const it of itens) {
            metodos.push({
                numero: it.numero,
                metodo: it.metodo,
                justificativa: it.metodoJustificativa.trim() || undefined,
            })
            for (const l of it.linhas) {
                const v = Number(l.valor)
                ajustes.push({
                    numero: it.numero,
                    id_coleta: l.id_coleta,
                    status: l.usar ? 'UTILIZADO' : 'DESCARTADO',
                    valor_unitario_equalizado: Number.isFinite(v) && l.valor !== '' ? v : undefined,
                })
            }
            const nv = Number(it.novoValor)
            if (it.novoFornecedor.trim() && Number.isFinite(nv) && nv > 0) {
                novos.push({
                    numero: it.numero,
                    fornecedor_nome: it.novoFornecedor.trim(),
                    valor_unitario_equalizado: nv,
                })
            }
        }
        setSalvando(true)
        try {
            const d = await api.revisar(detail.id, {ajustes, novos, metodos})
            onUpdated(d)
        } catch (e) {
            console.error('Erro ao recalcular:', e)
            alert('Não foi possível recalcular a cesta.')
        } finally {
            setSalvando(false)
        }
    }

    return (
        <Card>
            <SectionTitle>Revisão da cesta (Art. 6º / Parâmetro IV)</SectionTitle>
            <Typography variant="body2" sx={{color: '#5b6b7b', mb: 2}}>
                Marque os preços que compõem a cesta, ajuste valores unitários ou adicione uma
                cotação direta (Parâmetro IV). Ao salvar, a estatística é recalculada e o
                relatório/PDF/Word passam a refletir a sua revisão.
            </Typography>
            {itens.map((it, ii) => {
                // Índice original preservado p/ que patchLinha continue mapeando it.linhas[li].
                const comIndex = it.linhas.map((l, li) => ({l, li}))
                const naoConfirmadas = comIndex.filter(({l}) => isBaseNaoConfirmada(l))
                const confirmadas = comIndex.filter(({l}) => !isBaseNaoConfirmada(l))
                return (
                <Box key={it.numero} sx={{mb: 3}}>
                    <Typography variant="subtitle2" sx={{fontWeight: 700, mb: 1}}>
                        Item {it.numero} — {it.descricao}
                    </Typography>
                    {it.banner && (
                        <Alert
                            severity="info"
                            icon={false}
                            role="note"
                            sx={{py: 0.25, mb: 1.5, '& .MuiAlert-message': {py: 0.5}}}
                        >
                            <Typography variant="caption" sx={{color: '#334155'}}>
                                {it.banner.unidadeBase && (
                                    <>
                                        Comparado por <strong>{it.banner.unidadeBase}</strong> —{' '}
                                    </>
                                )}
                                vendido majoritariamente por{' '}
                                <strong>{it.banner.unidadeDominante}</strong>
                                {it.banner.m > 0
                                    ? ` (${it.banner.n} de ${it.banner.m} contratações)`
                                    : ''}
                            </Typography>
                        </Alert>
                    )}
                    <Box sx={{mb: 1, maxWidth: 360}}>
                        <FieldLabel>Método do valor estimado</FieldLabel>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            value={it.metodo}
                            onChange={(e) => patchItem(ii, {metodo: e.target.value})}
                        >
                            <MenuItem value="AUTO">Automático (média/mediana por dispersão)</MenuItem>
                            <MenuItem value="MEDIA">Média</MenuItem>
                            <MenuItem value="MEDIANA">Mediana</MenuItem>
                            <MenuItem value="MENOR">Menor preço</MenuItem>
                        </TextField>
                        {it.metodo !== 'AUTO' && (
                            <Box sx={{mt: 1}}>
                                <FieldLabel>Justificativa do método (art. 6º — registrada no relatório)</FieldLabel>
                                <TextField
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={it.metodoJustificativa}
                                    onChange={(e) => patchItem(ii, {metodoJustificativa: e.target.value})}
                                    placeholder="Ex.: mercado homogêneo; o menor preço é exequível e atende ao interesse público."
                                />
                            </Box>
                        )}
                    </Box>
                    <TabelaRevisao linhas={confirmadas} ii={ii} patchLinha={patchLinha} />
                    {naoConfirmadas.length > 0 && (
                        <Box sx={{mt: 2}}>
                            <Alert severity="warning" role="note" sx={{mb: 1}}>
                                <Typography variant="caption" sx={{display: 'block', fontWeight: 700}}>
                                    Base de comparação não confirmada
                                </Typography>
                                <Typography variant="caption" sx={{color: '#5b6b7b'}}>
                                    A unidade/embalagem destes preços não foi confirmada, então a
                                    padronização foi assumida. Os desmarcados ficaram FORA do cálculo;
                                    os que continuam marcados entram sobre uma base assumida — revise
                                    antes de confiar no valor.
                                </Typography>
                            </Alert>
                            <TabelaRevisao linhas={naoConfirmadas} ii={ii} patchLinha={patchLinha} />
                        </Box>
                    )}
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center', mt: 1.5, flexWrap: 'wrap'}}>
                        <Typography variant="caption" sx={{color: '#5b6b7b'}}>
                            + Cotação direta (Parâmetro IV):
                        </Typography>
                        <TextField
                            size="small"
                            placeholder="Fornecedor"
                            value={it.novoFornecedor}
                            onChange={(e) => patchItem(ii, {novoFornecedor: e.target.value})}
                        />
                        <TextField
                            size="small"
                            type="number"
                            placeholder="Valor unit."
                            value={it.novoValor}
                            onChange={(e) => patchItem(ii, {novoValor: e.target.value})}
                            onWheel={(e) => (e.target as HTMLElement).blur()}
                            sx={{width: 130}}
                        />
                    </Box>
                </Box>
                )
            })}
            <Button
                variant="contained"
                disabled={salvando}
                onClick={salvar}
                startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{textTransform: 'none', fontWeight: 600, backgroundColor: ACCENT, '&:hover': {backgroundColor: ACCENT}}}
            >
                {salvando ? 'Recalculando…' : 'Salvar revisão e recalcular'}
            </Button>
        </Card>
    )
}
